import React, { useEffect, useState, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import {
  Box,
  Typography,
  Button,
  Container,
  Grid,
  Card,
  CardContent,
  CardActions,
  useTheme,
  useMediaQuery,
  Paper,
  Avatar,
  AppBar,
  Toolbar,
  IconButton,
  CircularProgress,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  DialogContentText,
  TextField,
  InputAdornment,
  Snackbar,
  Alert,
  Checkbox,
  FormControlLabel,
  FormControl,
  InputLabel,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Autocomplete,
  Chip,
} from '@mui/material';
import {
  Business as BusinessIcon,
  Receipt as ReceiptIcon,
  Assignment as AssignmentIcon,
  Dashboard as DashboardIcon,
  TrendingUp as TrendingUpIcon,
  Security as SecurityIcon,
  Logout as LogoutIcon,
  Person as PersonIcon,
  LocalOffer as DiscountIcon,
  ConfirmationNumber as PromoCodeIcon,
  MoreVert as MoreVertIcon,
  MeetingRoom as MeetingRoomIcon,
  Delete as DeleteIcon,
  Cancel as CancelIcon,
  AccountBalance as AccountBalanceIcon,
  Link as LinkIcon,
  Map as MapIcon,
  AccountBalanceWallet as AccountBalanceWalletIcon,
  Description as DescriptionIcon,
  CalendarMonth as CalendarIcon,
  Public as GoogleStatusIcon,
  MenuBook as BookingDetailsMenuIcon,
  FactCheck as RoomTypeApprovalsMenuIcon,
  Language as SitemapIcon,
  StarRate as StarRateIcon,
  Schedule as ScheduleIcon,
  Assessment as AssessmentIcon,
  SwapHoriz as TransferBookingMenuIcon,
  Search as SearchIcon,
  HowToReg as SfOnboardingMenuIcon,
  TableChart as GhaExcelMenuIcon,
} from '@mui/icons-material';
import { addNewRoomCategory, deleteRoomCategory, searchRoomCategories, getRoomTypesForHotelAdmin, deleteRoomTypeAdmin, cancelBooking, cancelBookingInstantRefund, sendCancelWarningToHotel, setCompensationCreditNote, updateLinkedAccount, mapTabtripsWithStayflexi, generateVoucher, markInvoiceAsSettled, setMapsAiPricingUndercutInr, cronSetHotelNearByPlaces, setStayflexiChannelConfigToken, updateSitemap, addManualHotelReview, onboardStayflexiHotel } from '../api/adminPanelApi';
import { can, canIvrC2c, canIvrHub, FEATURES } from '../utils/permissions';
import AdminPanelSettingsIcon from '@mui/icons-material/AdminPanelSettings';
import PhoneInTalkIcon from '@mui/icons-material/PhoneInTalk';
import { IvrHeaderCallButton } from './IvrClickToCall';

const formatDateTimeLocal = (date = new Date()) => {
  const pad = (n) => String(n).padStart(2, '0');
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(date.getHours())}:${pad(date.getMinutes())}`;
};

const formatInsertTimeForApi = (dateTimeLocal) => {
  const [datePart, timePart] = dateTimeLocal.split('T');
  return `${datePart} ${timePart}:00`;
};

const HomePage = () => {
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const { user, logout, isAuthenticated, loading } = useAuth();
  
  // Menu and dialog states
  const [menuAnchorEl, setMenuAnchorEl] = useState(null);
  const [addRoomDialogOpen, setAddRoomDialogOpen] = useState(false);
  const [deleteRoomDialogOpen, setDeleteRoomDialogOpen] = useState(false);
  const [cancelBookingDialogOpen, setCancelBookingDialogOpen] = useState(false);
  const [cancelInstantRefundDialogOpen, setCancelInstantRefundDialogOpen] = useState(false);
  const [cancelInstantRefundBookingId, setCancelInstantRefundBookingId] = useState('');
  const [cancelInstantRefundLoading, setCancelInstantRefundLoading] = useState(false);
  const [compensationDialogOpen, setCompensationDialogOpen] = useState(false);
  const [updateLinkedAccountDialogOpen, setUpdateLinkedAccountDialogOpen] = useState(false);
  const [mapTabtripsStayflexiDialogOpen, setMapTabtripsStayflexiDialogOpen] = useState(false);
  const [generateVoucherDialogOpen, setGenerateVoucherDialogOpen] = useState(false);
  const [roomName, setRoomName] = useState('');
  const [categorySearchInput, setCategorySearchInput] = useState('');
  const [categorySearchResults, setCategorySearchResults] = useState([]);
  const [categorySearchLoading, setCategorySearchLoading] = useState(false);
  const [categorySearchError, setCategorySearchError] = useState('');
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [categoryInUseDialogOpen, setCategoryInUseDialogOpen] = useState(false);
  const [categoryInUseData, setCategoryInUseData] = useState(null);
  const [categoryForceDeleting, setCategoryForceDeleting] = useState(false);
  const [categoryPartialFailure, setCategoryPartialFailure] = useState(null);
  const categorySearchDebounceRef = useRef(null);
  const [hotelId, setHotelId] = useState('');
  const [bookingId, setBookingId] = useState('');
  const [compensationHotelId, setCompensationHotelId] = useState('');
  const [compensationMonth, setCompensationMonth] = useState('');
  const [compensationAmount, setCompensationAmount] = useState('');
  const [linkedAccountHotelId, setLinkedAccountHotelId] = useState('');
  const [tabtripsId, setTabtripsId] = useState('');
  const [stayFlexiId, setStayFlexiId] = useState('');
  const [isUseStayflexi, setIsUseStayflexi] = useState('no');
  const [addRoomLoading, setAddRoomLoading] = useState(false);
  const [deleteRoomLoading, setDeleteRoomLoading] = useState(false);
  const [cancelBookingLoading, setCancelBookingLoading] = useState(false);
  const [compensationLoading, setCompensationLoading] = useState(false);
  const [updateLinkedAccountLoading, setUpdateLinkedAccountLoading] = useState(false);
  const [mapTabtripsStayflexiLoading, setMapTabtripsStayflexiLoading] = useState(false);
  const [voucherBookingId, setVoucherBookingId] = useState('');
  const [generatingGuestVoucher, setGeneratingGuestVoucher] = useState(false);
  const [generatingHotelierVoucher, setGeneratingHotelierVoucher] = useState(false);
  const [settleInvoiceDialogOpen, setSettleInvoiceDialogOpen] = useState(false);
  const [settleInvoiceHotelId, setSettleInvoiceHotelId] = useState('');
  const [settleInvoiceMonth, setSettleInvoiceMonth] = useState('');
  const [settleInvoiceLoading, setSettleInvoiceLoading] = useState(false);
  const [deleteRoomTypeDialogOpen, setDeleteRoomTypeDialogOpen] = useState(false);
  const [aiPricingDiscountDialogOpen, setAiPricingDiscountDialogOpen] = useState(false);
  const [aiPricingDiscountInr, setAiPricingDiscountInr] = useState('');
  const [aiPricingDiscountLoading, setAiPricingDiscountLoading] = useState(false);
  const [nearbyPlacesDialogOpen, setNearbyPlacesDialogOpen] = useState(false);
  const [nearbyPlacesHotelId, setNearbyPlacesHotelId] = useState('');
  const [nearbyPlacesLoading, setNearbyPlacesLoading] = useState(false);
  const [stayflexiMappingTokenDialogOpen, setStayflexiMappingTokenDialogOpen] = useState(false);
  const [stayflexiMappingToken, setStayflexiMappingToken] = useState('');
  const [stayflexiMappingTokenLoading, setStayflexiMappingTokenLoading] = useState(false);
  const [deleteRoomTypeHotelId, setDeleteRoomTypeHotelId] = useState('');
  const [roomTypesList, setRoomTypesList] = useState([]);
  const [roomTypesHotelName, setRoomTypesHotelName] = useState('');
  const [roomTypesLoading, setRoomTypesLoading] = useState(false);
  const [deleteRoomTypeConfirm, setDeleteRoomTypeConfirm] = useState(null);
  const [deleteRoomTypeDeleting, setDeleteRoomTypeDeleting] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [updateSitemapLoading, setUpdateSitemapLoading] = useState(false);
  const [stayflexiOnboardingDialogOpen, setStayflexiOnboardingDialogOpen] = useState(false);
  const [stayflexiOnboardingHotelId, setStayflexiOnboardingHotelId] = useState('');
  const [stayflexiOnboardingLoading, setStayflexiOnboardingLoading] = useState(false);
  const [addRatingDialogOpen, setAddRatingDialogOpen] = useState(false);
  const [addRatingLoading, setAddRatingLoading] = useState(false);
  const [addRatingHotelId, setAddRatingHotelId] = useState('');
  const [addRatingGuestName, setAddRatingGuestName] = useState('');
  const [addRatingOverall, setAddRatingOverall] = useState('5');
  const [addRatingCleanliness, setAddRatingCleanliness] = useState('5');
  const [addRatingService, setAddRatingService] = useState('5');
  const [addRatingComfort, setAddRatingComfort] = useState('5');
  const [addRatingValue, setAddRatingValue] = useState('5');
  const [addRatingText, setAddRatingText] = useState('');
  const [addRatingInsertTime, setAddRatingInsertTime] = useState(() => formatDateTimeLocal());

  const adminPanels = [
    {
      title: 'Booking Panel',
      description: 'Search hotels, select rooms, and see live pricing with breakdown.',
      icon: <DashboardIcon sx={{ fontSize: 40 }} />,
      path: '/booking',
      featureKey: FEATURES.booking_panel,
      color: 'linear-gradient(135deg, #ffecd2 0%, #fcb69f 100%)',
      features: ['Instant Search', 'Dynamic Pricing', 'Room Selection', 'Amenities Overview']
    },
    {
      title: 'Bookings',
      description: 'View total sales and bookings. Filter by Prepaid or Postpaid and drill into hotel-level details.',
      icon: <BusinessIcon sx={{ fontSize: 40 }} />,
      path: '/bookings',
      featureKey: FEATURES.bookings,
      color: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
      features: ['Total Sales', 'Prepaid & Postpaid', 'Date Filters', 'Hotel Breakdown']
    },
    {
      title: 'User Assignment',
      description: 'Assign users to hotels, manage property activation status, and control user access permissions.',
      icon: <AssignmentIcon sx={{ fontSize: 40 }} />,
      path: '/user-assign',
      featureKey: FEATURES.user_assign,
      color: 'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)',
      features: ['User Management', 'Hotel Assignment', 'Property Control', 'Access Management']
    },
    {
      title: 'Discount Management',
      description: 'View and manage all hotel discounts, promotional offers, and special deals across properties.',
      icon: <DiscountIcon sx={{ fontSize: 40 }} />,
      path: '/discounts',
      featureKey: FEATURES.discounts,
      color: 'linear-gradient(135deg, #ff9a9e 0%, #fecfef 100%)',
      features: ['Discount Overview', 'Promotional Offers', 'Property Search', 'Deal Management']
    },
    {
      title: 'Promo Codes',
      description: 'Create and manage checkout promo codes: fixed amount, percent, and referral discounts.',
      icon: <PromoCodeIcon sx={{ fontSize: 40 }} />,
      path: '/promo-codes',
      featureKey: FEATURES.promo_codes,
      color: 'linear-gradient(135deg, #a8edea 0%, #fed6e3 100%)',
      features: ['Create Promos', 'Percent & Fixed', 'Validity & Caps', 'Usage Tracking']
    },
    {
      title: 'Hotel Accounts',
      description: 'View hotel accounts with Razorpay integration details and full account information.',
      icon: <AccountBalanceWalletIcon sx={{ fontSize: 40 }} />,
      path: '/hotel-accounts',
      featureKey: FEATURES.hotel_accounts,
      color: 'linear-gradient(135deg, #ffd89b 0%, #19547b 100%)',
      features: ['Account Overview', 'Razorpay Integration', 'Account Details', 'Search & Filter']
    },
    {
      title: 'Payment Settlements',
      description: 'View payment settlements with booking details, financial information, and account status.',
      icon: <ReceiptIcon sx={{ fontSize: 40 }} />,
      path: '/payment-settlements',
      featureKey: FEATURES.payment_settlements,
      color: 'linear-gradient(135deg, #ff6b6b 0%, #ee5a6f 100%)',
      features: ['Settlement Overview', 'Financial Details', 'Account Info', 'Booking Status']
    },
    {
      title: 'Hotel Bank Details',
      description: 'View and compare hotel bank details with route account status and detect discrepancies.',
      icon: <AccountBalanceWalletIcon sx={{ fontSize: 40 }} />,
      path: '/hotel-bank-details',
      featureKey: FEATURES.hotel_bank_details,
      color: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
      features: ['Bank Details', 'Route Status', 'Details Comparison', 'Change Detection']
    },
    {
      title: 'Finance',
      description: 'GST filing report, property-wise GST breakdown, settlements, and booking audit for monthly compliance.',
      icon: <AssessmentIcon sx={{ fontSize: 40 }} />,
      path: '/finance',
      featureKey: FEATURES.finance,
      color: 'linear-gradient(135deg, #1a237e 0%, #3949ab 100%)',
      features: ['GST Summary', 'Property GSTIN', 'Settlements', 'Excel Export']
    }
  ];

  const visiblePanels = adminPanels.filter((p) => can(user, p.featureKey));
  const showOverflowMenu =
    can(user, FEATURES.booking_details) ||
    can(user, FEATURES.transfer_booking) ||
    can(user, FEATURES.sf_onboarding) ||
    can(user, FEATURES.gha_excel) ||
    can(user, FEATURES.room_type_approvals) ||
    can(user, FEATURES.mapping_data) ||
    can(user, FEATURES.room_category_manage) ||
    can(user, FEATURES.compensation_credit_note) ||
    can(user, FEATURES.update_linked_account) ||
    can(user, FEATURES.generate_voucher) ||
    can(user, FEATURES.settle_invoice) ||
    can(user, FEATURES.ai_pricing_discount) ||
    can(user, FEATURES.nearby_places) ||
    can(user, FEATURES.stayflexi_mapping_token) ||
    can(user, FEATURES.google_status) ||
    can(user, FEATURES.gst_status) ||
    can(user, FEATURES.add_rating);

  const handleNavigate = (path) => {
    if (!isAuthenticated) {
      navigate('/login', { state: { from: path } });
    } else {
      navigate(path);
    }
  };

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  // Menu and dialog handlers
  const handleMenuOpen = (event) => {
    setMenuAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setMenuAnchorEl(null);
  };

  const handleAddRoomOpen = () => {
    setAddRoomDialogOpen(true);
    setRoomName('');
    handleMenuClose();
  };

  const handleDeleteRoomOpen = () => {
    setDeleteRoomDialogOpen(true);
    setCategorySearchInput('');
    setCategorySearchResults([]);
    setCategorySearchError('');
    setSelectedCategory(null);
    setCategoryInUseDialogOpen(false);
    setCategoryInUseData(null);
    setCategoryPartialFailure(null);
    handleMenuClose();
  };

  const handleCompensationOpen = () => {
    setCompensationDialogOpen(true);
    setCompensationHotelId('');
    setCompensationMonth('');
    setCompensationAmount('');
    handleMenuClose();
  };

  const handleUpdateLinkedAccountOpen = () => {
    setUpdateLinkedAccountDialogOpen(true);
    setLinkedAccountHotelId('');
    handleMenuClose();
  };

  const handleGenerateVoucherOpen = () => {
    setGenerateVoucherDialogOpen(true);
    setVoucherBookingId('');
    handleMenuClose();
  };

  const handleSettleInvoiceOpen = () => {
    setSettleInvoiceDialogOpen(true);
    setSettleInvoiceHotelId('');
    setSettleInvoiceMonth('');
    handleMenuClose();
  };

  const handleAddRoomClose = () => {
    setAddRoomDialogOpen(false);
    setRoomName('');
    setAddRoomLoading(false);
  };

  const handleDeleteRoomClose = () => {
    setDeleteRoomDialogOpen(false);
    setCategorySearchInput('');
    setCategorySearchResults([]);
    setCategorySearchError('');
    setSelectedCategory(null);
    setDeleteRoomLoading(false);
    setCategoryInUseDialogOpen(false);
    setCategoryInUseData(null);
    setCategoryForceDeleting(false);
    setCategoryPartialFailure(null);
    if (categorySearchDebounceRef.current) {
      clearTimeout(categorySearchDebounceRef.current);
      categorySearchDebounceRef.current = null;
    }
  };

  const runCategorySearch = useCallback(async (query) => {
    const q = (query || '').trim();
    if (!q) {
      setCategorySearchResults([]);
      setCategorySearchError('');
      setCategorySearchLoading(false);
      return;
    }
    setCategorySearchLoading(true);
    setCategorySearchError('');
    try {
      const res = await searchRoomCategories(q);
      if (res.success) {
        setCategorySearchResults(res.data || []);
        if (!(res.data || []).length) {
          setCategorySearchError('No room categories match your search.');
        }
      } else {
        setCategorySearchResults([]);
        setCategorySearchError(res.error || 'Search failed');
      }
    } catch (e) {
      setCategorySearchResults([]);
      setCategorySearchError(e.message || 'Search failed');
    } finally {
      setCategorySearchLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!deleteRoomDialogOpen) return undefined;
    if (categorySearchDebounceRef.current) {
      clearTimeout(categorySearchDebounceRef.current);
    }
    const q = categorySearchInput.trim();
    if (!q) {
      setCategorySearchResults([]);
      setCategorySearchError('');
      setCategorySearchLoading(false);
      return undefined;
    }
    setCategorySearchLoading(true);
    categorySearchDebounceRef.current = setTimeout(() => {
      runCategorySearch(q);
    }, 300);
    return () => {
      if (categorySearchDebounceRef.current) {
        clearTimeout(categorySearchDebounceRef.current);
      }
    };
  }, [categorySearchInput, deleteRoomDialogOpen, runCategorySearch]);

  const handleCancelBookingClose = () => {
    setCancelBookingDialogOpen(false);
    setBookingId('');
    setCancelBookingLoading(false);
  };

  const handleCancelInstantRefundClose = () => {
    setCancelInstantRefundDialogOpen(false);
    setCancelInstantRefundBookingId('');
    setCancelInstantRefundLoading(false);
  };

  const handleCompensationClose = () => {
    setCompensationDialogOpen(false);
    setCompensationHotelId('');
    setCompensationMonth('');
    setCompensationAmount('');
    setCompensationLoading(false);
  };

  const handleUpdateLinkedAccountClose = () => {
    setUpdateLinkedAccountDialogOpen(false);
    setLinkedAccountHotelId('');
    setUpdateLinkedAccountLoading(false);
  };

  const handleMapTabtripsStayflexiClose = () => {
    setMapTabtripsStayflexiDialogOpen(false);
    setTabtripsId('');
    setStayFlexiId('');
    setIsUseStayflexi('no');
    setMapTabtripsStayflexiLoading(false);
  };

  const handleGenerateVoucherClose = () => {
    setGenerateVoucherDialogOpen(false);
    setVoucherBookingId('');
    setGeneratingGuestVoucher(false);
    setGeneratingHotelierVoucher(false);
  };

  const handleSettleInvoiceClose = () => {
    setSettleInvoiceDialogOpen(false);
    setSettleInvoiceHotelId('');
    setSettleInvoiceMonth('');
    setSettleInvoiceLoading(false);
  };

  const handleAiPricingDiscountOpen = () => {
    setAiPricingDiscountDialogOpen(true);
    setAiPricingDiscountInr('');
    handleMenuClose();
  };

  const handleNearbyPlacesOpen = () => {
    setNearbyPlacesDialogOpen(true);
    setNearbyPlacesHotelId('');
    handleMenuClose();
  };

  const handleDeleteRoomTypeDialogClose = () => {
    setDeleteRoomTypeDialogOpen(false);
    setDeleteRoomTypeHotelId('');
    setRoomTypesList([]);
    setRoomTypesHotelName('');
    setDeleteRoomTypeConfirm(null);
    setRoomTypesLoading(false);
    setDeleteRoomTypeDeleting(false);
  };

  const handleAiPricingDiscountClose = () => {
    setAiPricingDiscountDialogOpen(false);
    setAiPricingDiscountInr('');
    setAiPricingDiscountLoading(false);
  };

  const handleNearbyPlacesClose = () => {
    setNearbyPlacesDialogOpen(false);
    setNearbyPlacesHotelId('');
    setNearbyPlacesLoading(false);
  };

  const handleStayflexiMappingTokenOpen = () => {
    setStayflexiMappingTokenDialogOpen(true);
    setStayflexiMappingToken('');
    handleMenuClose();
  };

  const handleStayflexiMappingTokenClose = () => {
    setStayflexiMappingTokenDialogOpen(false);
    setStayflexiMappingToken('');
    setStayflexiMappingTokenLoading(false);
  };

  const handleStayflexiMappingTokenSubmit = async () => {
    const token = stayflexiMappingToken.trim();
    if (!token) {
      setSnackbar({ open: true, message: 'Please enter a token', severity: 'warning' });
      return;
    }
    try {
      setStayflexiMappingTokenLoading(true);
      const response = await setStayflexiChannelConfigToken(token);
      if (response.success) {
        setSnackbar({
          open: true,
          message: response.data?.message || 'StayFlexi mapping token updated successfully',
          severity: 'success',
        });
        handleStayflexiMappingTokenClose();
      } else {
        setSnackbar({
          open: true,
          message: response.error || 'Failed to update StayFlexi mapping token',
          severity: 'error',
        });
      }
    } catch (error) {
      setSnackbar({
        open: true,
        message: error.message || 'Failed to update StayFlexi mapping token',
        severity: 'error',
      });
    } finally {
      setStayflexiMappingTokenLoading(false);
    }
  };

  const handleLoadRoomTypes = async () => {
    const hid = deleteRoomTypeHotelId.trim();
    if (!hid) {
      setSnackbar({ open: true, message: 'Please enter Hotel ID', severity: 'warning' });
      return;
    }
    setRoomTypesLoading(true);
    try {
      const res = await getRoomTypesForHotelAdmin(hid);
      if (res.success && res.data?.data) {
        const d = res.data.data;
        setRoomTypesList(d.room_types || []);
        setRoomTypesHotelName(d.hotel_name || '');
        setSnackbar({
          open: true,
          message: d.room_types?.length ? `Loaded ${d.room_types.length} room type(s)` : 'No room types found for this hotel',
          severity: d.room_types?.length ? 'success' : 'info'
        });
      } else {
        setSnackbar({ open: true, message: res.error || 'Failed to load room types', severity: 'error' });
        setRoomTypesList([]);
      }
    } catch (e) {
      setSnackbar({ open: true, message: e.message || 'Failed to load room types', severity: 'error' });
      setRoomTypesList([]);
    } finally {
      setRoomTypesLoading(false);
    }
  };

  const handleDeleteRoomTypeConfirmSubmit = async () => {
    if (!deleteRoomTypeConfirm) return;
    const { hotel_id, roomtype_id, roomtype_name } = deleteRoomTypeConfirm;
    setDeleteRoomTypeDeleting(true);
    try {
      const res = await deleteRoomTypeAdmin(hotel_id, roomtype_id);
      if (res.success) {
        setSnackbar({
          open: true,
          message: res.data?.message || `"${roomtype_name}" deleted successfully`,
          severity: 'success'
        });
        setDeleteRoomTypeConfirm(null);
        handleLoadRoomTypes();
      } else {
        setSnackbar({ open: true, message: res.error || 'Failed to delete room type', severity: 'error' });
      }
    } catch (e) {
      setSnackbar({ open: true, message: e.message || 'Failed to delete', severity: 'error' });
    } finally {
      setDeleteRoomTypeDeleting(false);
    }
  };

  const handleAiPricingDiscountSubmit = async () => {
    const discountValue = Number(aiPricingDiscountInr);
    if (!aiPricingDiscountInr.trim() || Number.isNaN(discountValue) || discountValue < 0) {
      setSnackbar({ open: true, message: 'Please enter a valid INR amount (0 or greater)', severity: 'warning' });
      return;
    }
    try {
      setAiPricingDiscountLoading(true);
      const response = await setMapsAiPricingUndercutInr(discountValue);
      if (response.success) {
        setSnackbar({
          open: true,
          message: response.data?.message || `AI pricing discount updated to INR ${discountValue}`,
          severity: 'success'
        });
        handleAiPricingDiscountClose();
      } else {
        setSnackbar({
          open: true,
          message: response.error || 'Failed to update AI pricing discount',
          severity: 'error'
        });
      }
    } catch (error) {
      setSnackbar({
        open: true,
        message: error.message || 'Failed to update AI pricing discount',
        severity: 'error'
      });
    } finally {
      setAiPricingDiscountLoading(false);
    }
  };

  const handleUpdateSitemap = async () => {
    try {
      setUpdateSitemapLoading(true);
      const response = await updateSitemap();
      if (response.success) {
        setSnackbar({
          open: true,
          message: response.message || 'Sitemap updated successfully',
          severity: 'success',
        });
      } else {
        setSnackbar({
          open: true,
          message: response.error || 'Failed to update sitemap',
          severity: 'error',
        });
      }
    } catch (error) {
      setSnackbar({
        open: true,
        message: error.message || 'Failed to update sitemap',
        severity: 'error',
      });
    } finally {
      setUpdateSitemapLoading(false);
    }
  };

  const handleStayflexiOnboardingOpen = () => {
    setStayflexiOnboardingDialogOpen(true);
    setStayflexiOnboardingHotelId('');
  };

  const handleStayflexiOnboardingClose = () => {
    setStayflexiOnboardingDialogOpen(false);
    setStayflexiOnboardingHotelId('');
    setStayflexiOnboardingLoading(false);
  };

  const handleStayflexiOnboardingSubmit = async () => {
    const hid = stayflexiOnboardingHotelId.trim();
    if (!hid) {
      setSnackbar({ open: true, message: 'Please enter Hotel ID', severity: 'warning' });
      return;
    }
    const hotelIdNum = Number(hid);
    if (Number.isNaN(hotelIdNum) || hotelIdNum <= 0) {
      setSnackbar({ open: true, message: 'Please enter a valid Hotel ID', severity: 'warning' });
      return;
    }
    try {
      setStayflexiOnboardingLoading(true);
      const response = await onboardStayflexiHotel(hotelIdNum);
      if (response.success) {
        setSnackbar({
          open: true,
          message: response.message || `Hotel ${hotelIdNum} onboarded on Stayflexi`,
          severity: 'success',
        });
        handleStayflexiOnboardingClose();
      } else {
        setSnackbar({
          open: true,
          message: response.error || 'Failed to onboard hotel on Stayflexi',
          severity: 'error',
        });
      }
    } catch (error) {
      setSnackbar({
        open: true,
        message: error.message || 'Failed to onboard hotel on Stayflexi',
        severity: 'error',
      });
    } finally {
      setStayflexiOnboardingLoading(false);
    }
  };

  const resetAddRatingForm = () => {
    setAddRatingHotelId('');
    setAddRatingGuestName('');
    setAddRatingOverall('5');
    setAddRatingCleanliness('5');
    setAddRatingService('5');
    setAddRatingComfort('5');
    setAddRatingValue('5');
    setAddRatingText('');
    setAddRatingInsertTime(formatDateTimeLocal());
    setAddRatingLoading(false);
  };

  const handleAddRatingOpen = () => {
    setAddRatingDialogOpen(true);
    resetAddRatingForm();
    handleMenuClose();
  };

  const handleAddRatingClose = () => {
    setAddRatingDialogOpen(false);
    resetAddRatingForm();
  };

  const parseRating = (value, label) => {
    const num = Number(value);
    if (Number.isNaN(num) || num < 1 || num > 5 || !Number.isInteger(num)) {
      return { valid: false, message: `${label} must be a whole number between 1 and 5` };
    }
    return { valid: true, value: num };
  };

  const handleAddRatingInsertTimeChange = (e) => {
    const value = e.target.value;
    if (!value) {
      setAddRatingInsertTime('');
      return;
    }
    const selected = new Date(value);
    const now = new Date();
    if (selected > now) {
      setAddRatingInsertTime(formatDateTimeLocal(now));
      setSnackbar({ open: true, message: 'Insert time cannot be in the future', severity: 'warning' });
      return;
    }
    setAddRatingInsertTime(value);
  };

  const handleAddRatingSubmit = async () => {
    const hid = addRatingHotelId.trim();
    const guestName = addRatingGuestName.trim();
    const reviewText = addRatingText.trim();

    if (!hid) {
      setSnackbar({ open: true, message: 'Please enter Hotel ID', severity: 'warning' });
      return;
    }
    const hotelIdNum = Number(hid);
    if (Number.isNaN(hotelIdNum) || hotelIdNum <= 0) {
      setSnackbar({ open: true, message: 'Please enter a valid Hotel ID', severity: 'warning' });
      return;
    }
    if (!guestName) {
      setSnackbar({ open: true, message: 'Please enter guest name', severity: 'warning' });
      return;
    }
    if (!reviewText) {
      setSnackbar({ open: true, message: 'Please enter review text', severity: 'warning' });
      return;
    }
    if (!addRatingInsertTime) {
      setSnackbar({ open: true, message: 'Please select insert time', severity: 'warning' });
      return;
    }
    const insertDate = new Date(addRatingInsertTime);
    if (insertDate > new Date()) {
      setSnackbar({ open: true, message: 'Insert time cannot be in the future', severity: 'warning' });
      return;
    }

    const ratings = [
      { value: addRatingOverall, label: 'Overall rating' },
      { value: addRatingCleanliness, label: 'Cleanliness rating' },
      { value: addRatingService, label: 'Service rating' },
      { value: addRatingComfort, label: 'Comfort rating' },
      { value: addRatingValue, label: 'Value rating' },
    ];
    const parsedRatings = {};
    for (const rating of ratings) {
      const result = parseRating(rating.value, rating.label);
      if (!result.valid) {
        setSnackbar({ open: true, message: result.message, severity: 'warning' });
        return;
      }
      parsedRatings[rating.label] = result.value;
    }

    try {
      setAddRatingLoading(true);
      const response = await addManualHotelReview({
        hotel_id: hotelIdNum,
        guest_name: guestName,
        overall_rating: parsedRatings['Overall rating'],
        cleanliness_rating: parsedRatings['Cleanliness rating'],
        service_rating: parsedRatings['Service rating'],
        comfort_rating: parsedRatings['Comfort rating'],
        value_rating: parsedRatings['Value rating'],
        review_text: reviewText,
        insert_time: formatInsertTimeForApi(addRatingInsertTime),
      });
      if (response.success) {
        setSnackbar({
          open: true,
          message: response.data?.message || 'Review added successfully',
          severity: 'success',
        });
        handleAddRatingClose();
      } else {
        setSnackbar({
          open: true,
          message: response.error || 'Failed to add review',
          severity: 'error',
        });
      }
    } catch (error) {
      setSnackbar({
        open: true,
        message: error.message || 'Failed to add review',
        severity: 'error',
      });
    } finally {
      setAddRatingLoading(false);
    }
  };

  const handleNearbyPlacesSubmit = async () => {
    const hid = nearbyPlacesHotelId.trim();
    if (!hid) {
      setSnackbar({ open: true, message: 'Please enter Hotel ID', severity: 'warning' });
      return;
    }
    const hotelIdNum = Number(hid);
    if (Number.isNaN(hotelIdNum) || hotelIdNum <= 0) {
      setSnackbar({ open: true, message: 'Please enter a valid Hotel ID', severity: 'warning' });
      return;
    }
    try {
      setNearbyPlacesLoading(true);
      const response = await cronSetHotelNearByPlaces(hotelIdNum);
      if (response.success) {
        setSnackbar({
          open: true,
          message: response.data?.message || `Nearby places setup triggered for hotel ${hotelIdNum}`,
          severity: 'success'
        });
        handleNearbyPlacesClose();
      } else {
        setSnackbar({
          open: true,
          message: response.error || 'Failed to set nearby places',
          severity: 'error'
        });
      }
    } catch (error) {
      setSnackbar({
        open: true,
        message: error.message || 'Failed to set nearby places',
        severity: 'error'
      });
    } finally {
      setNearbyPlacesLoading(false);
    }
  };

  // Convert base64 to PDF and download
  const downloadPDF = (base64String, fileName) => {
    try {
      const byteCharacters = atob(base64String);
      const byteNumbers = new Array(byteCharacters.length);
      for (let i = 0; i < byteCharacters.length; i++) {
        byteNumbers[i] = byteCharacters.charCodeAt(i);
      }
      const byteArray = new Uint8Array(byteNumbers);
      const blob = new Blob([byteArray], { type: 'application/pdf' });
      
      // Create download link
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = fileName;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Error downloading PDF:', error);
      throw new Error('Failed to download PDF');
    }
  };

  // Generate voucher handler
  const handleGenerateVoucher = async (voucherType) => {
    if (!voucherBookingId.trim()) {
      setSnackbar({
        open: true,
        message: 'Please enter Booking ID',
        severity: 'warning'
      });
      return;
    }

    const setLoading = voucherType === 'guest' ? setGeneratingGuestVoucher : setGeneratingHotelierVoucher;
    
    try {
      setLoading(true);
      
      const response = await generateVoucher(voucherBookingId.trim(), voucherType);
      
      if (response.success) {
        const fileName = response.data.file_name || `${voucherType === 'guest' ? 'Guest' : 'Hotelier'}_Voucher_${voucherBookingId}.pdf`;
        downloadPDF(response.data.pdf_base64, fileName);
        
        setSnackbar({
          open: true,
          message: `${voucherType === 'guest' ? 'Guest' : 'Hotelier'} voucher generated and downloaded successfully!`,
          severity: 'success'
        });
      } else {
        setSnackbar({
          open: true,
          message: response.error || `Failed to generate ${voucherType} voucher`,
          severity: 'error'
        });
      }
    } catch (error) {
      console.error(`Error generating ${voucherType} voucher:`, error);
      setSnackbar({
        open: true,
        message: error.message || `An error occurred while generating ${voucherType} voucher`,
        severity: 'error'
      });
    } finally {
      setLoading(false);
    }
  };

  const handleAddRoomType = async () => {
    if (!roomName.trim()) {
      setSnackbar({
        open: true,
        message: 'Please enter a room name',
        severity: 'warning'
      });
      return;
    }

    try {
      setAddRoomLoading(true);
      const response = await addNewRoomCategory(roomName.trim());

      if (response.success) {
        setSnackbar({
          open: true,
          message: 'Room type added successfully!',
          severity: 'success'
        });
        handleAddRoomClose();
      } else {
        setSnackbar({
          open: true,
          message: response.error || 'Failed to add room type',
          severity: 'error'
        });
      }
    } catch (error) {
      console.error('Error adding room type:', error);
      setSnackbar({
        open: true,
        message: 'An unexpected error occurred while adding room type',
        severity: 'error'
      });
    } finally {
      setAddRoomLoading(false);
    }
  };

  const handleDeleteRoomType = async () => {
    if (!selectedCategory?.category_id) {
      setSnackbar({
        open: true,
        message: 'Please search and select a room category',
        severity: 'warning',
      });
      return;
    }

    try {
      setDeleteRoomLoading(true);
      setCategoryPartialFailure(null);
      const response = await deleteRoomCategory({ category_id: selectedCategory.category_id });

      if (response.success) {
        setSnackbar({
          open: true,
          message: response.message || 'Room category deleted successfully!',
          severity: 'success',
        });
        handleDeleteRoomClose();
        return;
      }

      if (response.inUse || response.code === 'IN_USE') {
        setCategoryInUseData(response.data || {});
        setCategoryInUseDialogOpen(true);
        return;
      }

      setSnackbar({
        open: true,
        message: response.error || 'Failed to delete room category',
        severity: 'error',
      });
    } catch (error) {
      console.error('Error deleting room category:', error);
      setSnackbar({
        open: true,
        message: 'An unexpected error occurred while deleting room category',
        severity: 'error',
      });
    } finally {
      setDeleteRoomLoading(false);
    }
  };

  const handleForceDeleteRoomCategory = async () => {
    const categoryId = categoryInUseData?.category?.category_id ?? selectedCategory?.category_id;
    if (!categoryId) return;

    try {
      setCategoryForceDeleting(true);
      setCategoryPartialFailure(null);
      const response = await deleteRoomCategory({
        category_id: categoryId,
        force: true,
        confirm: true,
      });

      if (response.success) {
        setSnackbar({
          open: true,
          message: response.message || 'Room category deleted from all hotels.',
          severity: 'success',
        });
        handleDeleteRoomClose();
        return;
      }

      if (response.partialFailure || response.code === 'PARTIAL_FAILURE') {
        setCategoryPartialFailure(response.data || {});
        setSnackbar({
          open: true,
          message: response.error || 'Some hotels could not be updated.',
          severity: 'warning',
        });
        return;
      }

      setSnackbar({
        open: true,
        message: response.error || 'Failed to delete room category',
        severity: 'error',
      });
    } catch (error) {
      console.error('Error force deleting room category:', error);
      setSnackbar({
        open: true,
        message: 'An unexpected error occurred while deleting room category',
        severity: 'error',
      });
    } finally {
      setCategoryForceDeleting(false);
    }
  };

  const handleSendWarning = async () => {
    if (!bookingId.trim()) {
      setSnackbar({
        open: true,
        message: 'Please enter Booking ID',
        severity: 'warning'
      });
      return;
    }

    try {
      setCancelBookingLoading(true);
      const warningResponse = await sendCancelWarningToHotel(bookingId.trim());
      
      if (warningResponse.success) {
        setSnackbar({
          open: true,
          message: `Warning sent successfully for booking ${bookingId}!`,
          severity: 'success'
        });
      } else {
        setSnackbar({
          open: true,
          message: warningResponse.error || 'Failed to send warning',
          severity: 'error'
        });
      }
    } catch (error) {
      console.error('Error sending warning:', error);
      setSnackbar({
        open: true,
        message: 'An error occurred while sending warning',
        severity: 'error'
      });
    } finally {
      setCancelBookingLoading(false);
    }
  };

  const handleCancelBooking = async () => {
    if (!bookingId.trim()) {
      setSnackbar({
        open: true,
        message: 'Please enter Booking ID',
        severity: 'warning'
      });
      return;
    }

    try {
      setCancelBookingLoading(true);
      const cancelResponse = await cancelBooking(bookingId.trim());
      
      if (cancelResponse.success) {
        setSnackbar({
          open: true,
          message: `Booking ${bookingId} cancelled successfully!`,
          severity: 'success'
        });
        handleCancelBookingClose();
      } else {
        setSnackbar({
          open: true,
          message: cancelResponse.error || 'Failed to cancel booking',
          severity: 'error'
        });
      }
    } catch (error) {
      console.error('Error canceling booking:', error);
      setSnackbar({
        open: true,
        message: 'An error occurred while canceling booking',
        severity: 'error'
      });
    } finally {
      setCancelBookingLoading(false);
    }
  };

  const handleCancelInstantRefundSubmit = async () => {
    if (!cancelInstantRefundBookingId.trim()) {
      setSnackbar({ open: true, message: 'Please enter Booking ID', severity: 'warning' });
      return;
    }
    try {
      setCancelInstantRefundLoading(true);
      const response = await cancelBookingInstantRefund(cancelInstantRefundBookingId.trim());
      if (response.success) {
        setSnackbar({
          open: true,
          message: `Booking ${cancelInstantRefundBookingId} cancelled and instant refund initiated.`,
          severity: 'success'
        });
        handleCancelInstantRefundClose();
      } else {
        setSnackbar({
          open: true,
          message: response.error || 'Failed to cancel booking with instant refund',
          severity: 'error'
        });
      }
    } catch (error) {
      setSnackbar({
        open: true,
        message: error.message || 'An error occurred',
        severity: 'error'
      });
    } finally {
      setCancelInstantRefundLoading(false);
    }
  };

  const handleCompensationSubmit = async () => {
    if (!compensationHotelId.trim()) {
      setSnackbar({
        open: true,
        message: 'Please enter Hotel ID',
        severity: 'warning'
      });
      return;
    }

    if (!compensationMonth.trim()) {
      setSnackbar({
        open: true,
        message: 'Please select a month',
        severity: 'warning'
      });
      return;
    }

    if (!compensationAmount.trim() || isNaN(Number(compensationAmount)) || Number(compensationAmount) <= 0) {
      setSnackbar({
        open: true,
        message: 'Please enter a valid amount',
        severity: 'warning'
      });
      return;
    }

    try {
      setCompensationLoading(true);
      
      const response = await setCompensationCreditNote(
        compensationHotelId.trim(),
        compensationMonth.trim(),
        compensationAmount.trim()
      );
      
      if (response.success) {
        setSnackbar({
          open: true,
          message: 'Compensation & Credit Note added successfully!',
          severity: 'success'
        });
        handleCompensationClose();
      } else {
        setSnackbar({
          open: true,
          message: response.error || 'Failed to add compensation & credit note',
          severity: 'error'
        });
      }
    } catch (error) {
      console.error('Error adding compensation & credit note:', error);
      setSnackbar({
        open: true,
        message: 'An error occurred while adding compensation & credit note',
        severity: 'error'
      });
    } finally {
      setCompensationLoading(false);
    }
  };

  const handleUpdateLinkedAccountSubmit = async () => {
    if (!linkedAccountHotelId.trim()) {
      setSnackbar({
        open: true,
        message: 'Please enter Hotel ID',
        severity: 'warning'
      });
      return;
    }

    try {
      setUpdateLinkedAccountLoading(true);
      
      const response = await updateLinkedAccount(linkedAccountHotelId.trim());
      
      if (response.success) {
        setSnackbar({
          open: true,
          message: 'Linked account updated successfully!',
          severity: 'success'
        });
        handleUpdateLinkedAccountClose();
      } else {
        setSnackbar({
          open: true,
          message: response.error || 'Failed to update linked account',
          severity: 'error'
        });
      }
    } catch (error) {
      console.error('Error updating linked account:', error);
      setSnackbar({
        open: true,
        message: 'An error occurred while updating linked account',
        severity: 'error'
      });
    } finally {
      setUpdateLinkedAccountLoading(false);
    }
  };

  const handleMapTabtripsStayflexiSubmit = async () => {
    if (!tabtripsId.trim()) {
      setSnackbar({
        open: true,
        message: 'Please enter TabTrips ID',
        severity: 'warning'
      });
      return;
    }

    if (!stayFlexiId.trim()) {
      setSnackbar({
        open: true,
        message: 'Please enter StayFlexi ID',
        severity: 'warning'
      });
      return;
    }

    try {
      setMapTabtripsStayflexiLoading(true);
      
      const response = await mapTabtripsWithStayflexi(
        tabtripsId.trim(),
        stayFlexiId.trim(),
        isUseStayflexi
      );
      
      if (response.success) {
        // Get success message from API response or use default
        const successMessage = response.data?.message || 'TabTrips and StayFlexi mapped successfully!';
        setSnackbar({
          open: true,
          message: successMessage,
          severity: 'success'
        });
        handleMapTabtripsStayflexiClose();
      } else {
        // Show the error message from API response
        const errorMessage = response.error || response.data?.message || response.data?.messgae || 'Failed to map TabTrips with StayFlexi';
        setSnackbar({
          open: true,
          message: errorMessage,
          severity: 'error'
        });
      }
    } catch (error) {
      console.error('Error mapping TabTrips with StayFlexi:', error);
      setSnackbar({
        open: true,
        message: error.message || 'An error occurred while mapping TabTrips with StayFlexi',
        severity: 'error'
      });
    } finally {
      setMapTabtripsStayflexiLoading(false);
    }
  };

  const handleSettleInvoiceSubmit = async () => {
    const hid = settleInvoiceHotelId.trim();
    if (!hid) {
      setSnackbar({ open: true, message: 'Please enter Hotel ID', severity: 'warning' });
      return;
    }
    const hotelIdNum = parseInt(hid, 10);
    if (isNaN(hotelIdNum) || hotelIdNum <= 0) {
      setSnackbar({ open: true, message: 'Please enter a valid Hotel ID', severity: 'warning' });
      return;
    }
    if (!settleInvoiceMonth) {
      setSnackbar({ open: true, message: 'Please select Month & Year', severity: 'warning' });
      return;
    }
    const maxMonth = getCurrentMonth();
    if (settleInvoiceMonth > maxMonth) {
      setSnackbar({ open: true, message: 'Cannot select a future month', severity: 'warning' });
      return;
    }
    try {
      setSettleInvoiceLoading(true);
      const response = await markInvoiceAsSettled(hotelIdNum, settleInvoiceMonth);
      if (response.success) {
        setSnackbar({ open: true, message: 'Invoice marked as settled successfully.', severity: 'success' });
        handleSettleInvoiceClose();
      } else {
        setSnackbar({ open: true, message: response.error || 'Failed to mark invoice as settled', severity: 'error' });
      }
    } catch (error) {
      setSnackbar({ open: true, message: error.message || 'An error occurred', severity: 'error' });
    } finally {
      setSettleInvoiceLoading(false);
    }
  };

  // Get current month in YYYY-MM format for max date
  const getCurrentMonth = () => {
    const now = new Date();
    const year = now.getFullYear();
    const month = String(now.getMonth() + 1).padStart(2, '0');
    return `${year}-${month}`;
  };

  // Redirect to login if not authenticated
  useEffect(() => {
    if (!isAuthenticated && !loading) {
      navigate('/login');
    }
  }, [isAuthenticated, loading, navigate]);

  // Show loading while checking authentication
  if (loading) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        minHeight="100vh"
        sx={{
          background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
        }}
      >
        <CircularProgress size={60} sx={{ color: 'white' }} />
      </Box>
    );
  }

  return (
    <Box
      sx={{
        minHeight: '100vh',
        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
        position: 'relative',
        overflow: 'hidden'
      }}
    >
      {/* Header with Logout */}
      {/* Debug: isAuthenticated = {isAuthenticated.toString()}, user = {JSON.stringify(user)} */}
      {isAuthenticated && (
        <AppBar 
          position="static" 
          sx={{ 
            background: 'rgba(255, 255, 255, 0.1)', 
            backdropFilter: 'blur(10px)',
            boxShadow: 'none',
            borderBottom: '1px solid rgba(255, 255, 255, 0.2)',
            position: 'relative',
            zIndex: 10
          }}
        >
          <Toolbar sx={{ justifyContent: 'space-between' }}>
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <PersonIcon sx={{ color: 'white', mr: 1 }} />
              <Typography variant="body2" sx={{ color: 'white' }}>
                Welcome, {user?.name || user?.email}
                {user?.is_superadmin ? ' (superadmin)' : ''}
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap', justifyContent: 'flex-end' }}>
              {user?.is_superadmin && (
                <Button
                  onClick={() => handleNavigate('/admin-users')}
                  startIcon={<AdminPanelSettingsIcon />}
                  sx={{
                    color: 'white',
                    '&:hover': { backgroundColor: 'rgba(255, 119, 0, 0.2)' },
                  }}
                >
                  Manage Users
                </Button>
              )}
              {canIvrHub(user) && (
                <Button
                  onClick={() => handleNavigate('/ivr')}
                  startIcon={<PhoneInTalkIcon />}
                  sx={{
                    color: 'white',
                    '&:hover': { backgroundColor: 'rgba(255, 119, 0, 0.2)' },
                  }}
                >
                  IVR
                </Button>
              )}
              {canIvrC2c(user) && <IvrHeaderCallButton />}
              {can(user, FEATURES.b2b_admin) && (
                <Button
                  component="a"
                  href="https://b2b.tabtrips.com/admin"
                  target="_blank"
                  rel="noopener noreferrer"
                  sx={{
                    color: 'white',
                    '&:hover': { backgroundColor: 'rgba(255, 119, 0, 0.2)' },
                  }}
                >
                  B2B Admin
                </Button>
              )}
              {can(user, FEATURES.promo_codes) && (
                <Button
                  onClick={() => handleNavigate('/promo-codes')}
                  startIcon={<PromoCodeIcon />}
                  sx={{
                    color: 'white',
                    '&:hover': { backgroundColor: 'rgba(255, 119, 0, 0.2)' },
                  }}
                >
                  Promo Codes
                </Button>
              )}
              {can(user, FEATURES.stayflexi_onboarding) && (
                <Button
                  onClick={handleStayflexiOnboardingOpen}
                  startIcon={<LinkIcon />}
                  sx={{
                    color: 'white',
                    '&:hover': { backgroundColor: 'rgba(255, 119, 0, 0.2)' },
                  }}
                >
                  Stayflexi Onboarding
                </Button>
              )}
              {can(user, FEATURES.update_sitemap) && (
                <Button
                  onClick={handleUpdateSitemap}
                  disabled={updateSitemapLoading}
                  startIcon={updateSitemapLoading ? <CircularProgress size={18} sx={{ color: 'white' }} /> : <SitemapIcon />}
                  sx={{
                    color: 'white',
                    '&:hover': { backgroundColor: 'rgba(255, 119, 0, 0.2)' },
                  }}
                >
                  {updateSitemapLoading ? 'Updating…' : 'Update Sitemap'}
                </Button>
              )}
              {showOverflowMenu && (
                <IconButton
                  onClick={handleMenuOpen}
                  sx={{
                    color: 'white',
                    backgroundColor: 'rgba(255, 119, 0, 0.2)',
                    '&:hover': {
                      backgroundColor: 'rgba(255, 119, 0, 0.3)',
                      transform: 'scale(1.05)',
                    },
                    transition: 'all 0.2s ease-in-out',
                  }}
                >
                  <MoreVertIcon />
                </IconButton>
              )}

              <Menu
                anchorEl={menuAnchorEl}
                open={Boolean(menuAnchorEl)}
                onClose={handleMenuClose}
                anchorOrigin={{
                  vertical: 'bottom',
                  horizontal: 'right',
                }}
                transformOrigin={{
                  vertical: 'top',
                  horizontal: 'right',
                }}
                PaperProps={{
                  sx: {
                    mt: 1,
                    boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)',
                    borderRadius: 2,
                    minWidth: 200,
                    border: '1px solid #e0e0e0',
                    maxHeight: '80vh',
                  },
                }}
              >
                {can(user, FEATURES.booking_details) && (
                  <MenuItem
                    onClick={() => {
                      handleMenuClose();
                      navigate('/booking-details');
                    }}
                    sx={{ py: 1.5, px: 2 }}
                  >
                    <ListItemIcon sx={{ color: '#FF7700', minWidth: 40 }}>
                      <BookingDetailsMenuIcon />
                    </ListItemIcon>
                    <ListItemText
                      primary="Booking Details"
                      sx={{ '& .MuiListItemText-primary': { fontWeight: 500, color: '#2E4057' } }}
                    />
                  </MenuItem>
                )}
                {can(user, FEATURES.transfer_booking) && (
                  <MenuItem
                    onClick={() => {
                      handleMenuClose();
                      navigate('/transfer-booking');
                    }}
                    sx={{ py: 1.5, px: 2 }}
                  >
                    <ListItemIcon sx={{ color: '#1565c0', minWidth: 40 }}>
                      <TransferBookingMenuIcon />
                    </ListItemIcon>
                    <ListItemText
                      primary="Transfer Booking"
                      sx={{ '& .MuiListItemText-primary': { fontWeight: 500, color: '#2E4057' } }}
                    />
                  </MenuItem>
                )}
                {can(user, FEATURES.sf_onboarding) && (
                  <MenuItem
                    onClick={() => {
                      handleMenuClose();
                      navigate('/sf-onboarding');
                    }}
                    sx={{ py: 1.5, px: 2 }}
                  >
                    <ListItemIcon sx={{ color: '#00897b', minWidth: 40 }}>
                      <SfOnboardingMenuIcon />
                    </ListItemIcon>
                    <ListItemText
                      primary="SF Onboarding"
                      sx={{ '& .MuiListItemText-primary': { fontWeight: 500, color: '#2E4057' } }}
                    />
                  </MenuItem>
                )}
                {can(user, FEATURES.gha_excel) && (
                  <MenuItem
                    onClick={() => {
                      handleMenuClose();
                      navigate('/gha-excel');
                    }}
                    sx={{ py: 1.5, px: 2 }}
                  >
                    <ListItemIcon sx={{ color: '#6a1b9a', minWidth: 40 }}>
                      <GhaExcelMenuIcon />
                    </ListItemIcon>
                    <ListItemText
                      primary="GHA Excel"
                      sx={{ '& .MuiListItemText-primary': { fontWeight: 500, color: '#2E4057' } }}
                    />
                  </MenuItem>
                )}
                {can(user, FEATURES.room_type_approvals) && (
                  <MenuItem
                    onClick={() => {
                      handleMenuClose();
                      navigate('/room-type-approvals');
                    }}
                    sx={{ py: 1.5, px: 2 }}
                  >
                    <ListItemIcon sx={{ color: '#00897b', minWidth: 40 }}>
                      <RoomTypeApprovalsMenuIcon />
                    </ListItemIcon>
                    <ListItemText
                      primary="Room type approvals"
                      sx={{ '& .MuiListItemText-primary': { fontWeight: 500, color: '#2E4057' } }}
                    />
                  </MenuItem>
                )}
                {can(user, FEATURES.mapping_data) && (
                  <MenuItem
                    onClick={() => {
                      handleMenuClose();
                      navigate('/mapping-data');
                    }}
                    sx={{ py: 1.5, px: 2 }}
                  >
                    <ListItemIcon sx={{ color: '#2E4057', minWidth: 40 }}>
                      <MapIcon />
                    </ListItemIcon>
                    <ListItemText
                      primary="Mapping Data"
                      sx={{ '& .MuiListItemText-primary': { fontWeight: 500, color: '#2E4057' } }}
                    />
                  </MenuItem>
                )}
                {can(user, FEATURES.room_category_manage) && (
                  <MenuItem onClick={handleAddRoomOpen} sx={{ py: 1.5, px: 2 }}>
                    <ListItemIcon sx={{ color: '#FF7700', minWidth: 40 }}>
                      <MeetingRoomIcon />
                    </ListItemIcon>
                    <ListItemText
                      primary="Add Room Type"
                      sx={{ '& .MuiListItemText-primary': { fontWeight: 500, color: '#2E4057' } }}
                    />
                  </MenuItem>
                )}
                {can(user, FEATURES.room_category_manage) && (
                  <MenuItem onClick={handleDeleteRoomOpen} sx={{ py: 1.5, px: 2 }}>
                    <ListItemIcon sx={{ color: '#f44336', minWidth: 40 }}>
                      <DeleteIcon />
                    </ListItemIcon>
                    <ListItemText
                      primary="Delete Room Category"
                      sx={{ '& .MuiListItemText-primary': { fontWeight: 500, color: '#2E4057' } }}
                    />
                  </MenuItem>
                )}
                {can(user, FEATURES.compensation_credit_note) && (
                  <MenuItem onClick={handleCompensationOpen} sx={{ py: 1.5, px: 2 }}>
                    <ListItemIcon sx={{ color: '#9c27b0', minWidth: 40 }}>
                      <ReceiptIcon />
                    </ListItemIcon>
                    <ListItemText
                      primary="Compensation & Credit Note"
                      sx={{ '& .MuiListItemText-primary': { fontWeight: 500, color: '#2E4057' } }}
                    />
                  </MenuItem>
                )}
                {can(user, FEATURES.update_linked_account) && (
                  <MenuItem onClick={handleUpdateLinkedAccountOpen} sx={{ py: 1.5, px: 2 }}>
                    <ListItemIcon sx={{ color: '#2196f3', minWidth: 40 }}>
                      <LinkIcon />
                    </ListItemIcon>
                    <ListItemText
                      primary="Update Linked Account"
                      sx={{ '& .MuiListItemText-primary': { fontWeight: 500, color: '#2E4057' } }}
                    />
                  </MenuItem>
                )}
                {can(user, FEATURES.generate_voucher) && (
                  <MenuItem onClick={handleGenerateVoucherOpen} sx={{ py: 1.5, px: 2 }}>
                    <ListItemIcon sx={{ color: '#9c27b0', minWidth: 40 }}>
                      <DescriptionIcon />
                    </ListItemIcon>
                    <ListItemText
                      primary="Generate Voucher"
                      sx={{ '& .MuiListItemText-primary': { fontWeight: 500, color: '#2E4057' } }}
                    />
                  </MenuItem>
                )}
                {can(user, FEATURES.settle_invoice) && (
                  <MenuItem onClick={handleSettleInvoiceOpen} sx={{ py: 1.5, px: 2 }}>
                    <ListItemIcon sx={{ color: '#2e7d32', minWidth: 40 }}>
                      <ReceiptIcon />
                    </ListItemIcon>
                    <ListItemText
                      primary="Settle Invoice"
                      sx={{ '& .MuiListItemText-primary': { fontWeight: 500, color: '#2E4057' } }}
                    />
                  </MenuItem>
                )}
                {can(user, FEATURES.ai_pricing_discount) && (
                  <MenuItem onClick={handleAiPricingDiscountOpen} sx={{ py: 1.5, px: 2 }}>
                    <ListItemIcon sx={{ color: '#6a1b9a', minWidth: 40 }}>
                      <DiscountIcon />
                    </ListItemIcon>
                    <ListItemText
                      primary="AI Pricing Discount"
                      sx={{ '& .MuiListItemText-primary': { fontWeight: 500, color: '#2E4057' } }}
                    />
                  </MenuItem>
                )}
                {can(user, FEATURES.nearby_places) && (
                  <MenuItem onClick={handleNearbyPlacesOpen} sx={{ py: 1.5, px: 2 }}>
                    <ListItemIcon sx={{ color: '#2e7d32', minWidth: 40 }}>
                      <MapIcon />
                    </ListItemIcon>
                    <ListItemText
                      primary="Set Nearby Places"
                      sx={{ '& .MuiListItemText-primary': { fontWeight: 500, color: '#2E4057' } }}
                    />
                  </MenuItem>
                )}
                {can(user, FEATURES.stayflexi_mapping_token) && (
                  <MenuItem onClick={handleStayflexiMappingTokenOpen} sx={{ py: 1.5, px: 2 }}>
                    <ListItemIcon sx={{ color: '#00838f', minWidth: 40 }}>
                      <SecurityIcon />
                    </ListItemIcon>
                    <ListItemText
                      primary="Stayflexi Mapping Token"
                      sx={{ '& .MuiListItemText-primary': { fontWeight: 500, color: '#2E4057' } }}
                    />
                  </MenuItem>
                )}
                {can(user, FEATURES.google_status) && (
                  <MenuItem
                    onClick={() => {
                      handleMenuClose();
                      navigate('/google-status');
                    }}
                    sx={{ py: 1.5, px: 2 }}
                  >
                    <ListItemIcon sx={{ color: '#4285f4', minWidth: 40 }}>
                      <GoogleStatusIcon />
                    </ListItemIcon>
                    <ListItemText
                      primary="Google Status"
                      sx={{ '& .MuiListItemText-primary': { fontWeight: 500, color: '#2E4057' } }}
                    />
                  </MenuItem>
                )}
                {can(user, FEATURES.gst_status) && (
                  <MenuItem
                    onClick={() => {
                      handleMenuClose();
                      navigate('/gst-status');
                    }}
                    sx={{ py: 1.5, px: 2 }}
                  >
                    <ListItemIcon sx={{ color: '#1565c0', minWidth: 40 }}>
                      <ReceiptIcon />
                    </ListItemIcon>
                    <ListItemText
                      primary="GST Status"
                      sx={{ '& .MuiListItemText-primary': { fontWeight: 500, color: '#2E4057' } }}
                    />
                  </MenuItem>
                )}
                {can(user, FEATURES.add_rating) && (
                  <MenuItem onClick={handleAddRatingOpen} sx={{ py: 1.5, px: 2 }}>
                    <ListItemIcon sx={{ color: '#f9a825', minWidth: 40 }}>
                      <StarRateIcon />
                    </ListItemIcon>
                    <ListItemText
                      primary="Add Rating"
                      sx={{ '& .MuiListItemText-primary': { fontWeight: 500, color: '#2E4057' } }}
                    />
                  </MenuItem>
                )}
              </Menu>
              
              <Button
                onClick={handleLogout}
                variant="outlined"
                startIcon={<LogoutIcon />}
                sx={{ 
                  color: 'white',
                  borderColor: 'rgba(255, 119, 0, 0.5)',
                  background: 'rgba(255, 119, 0, 0.1)',
                  '&:hover': { 
                    background: 'rgba(255, 119, 0, 0.2)',
                    borderColor: 'rgba(255, 119, 0, 0.7)',
                  } 
                }}
              >
                Logout
              </Button>
            </Box>
          </Toolbar>
        </AppBar>
      )}

      {/* Background Elements */}
      <Box
        sx={{
          position: 'absolute',
          top: -50,
          right: -50,
          width: 200,
          height: 200,
          borderRadius: '50%',
          background: 'rgba(255, 255, 255, 0.1)',
          backdropFilter: 'blur(10px)',
          zIndex: 1
        }}
      />
      <Box
        sx={{
          position: 'absolute',
          bottom: -100,
          left: -100,
          width: 300,
          height: 300,
          borderRadius: '50%',
          background: 'rgba(255, 255, 255, 0.05)',
          backdropFilter: 'blur(10px)',
          zIndex: 1
        }}
      />

      <Container maxWidth="lg" sx={{ pt: { xs: 3, md: 5 }, pb: { xs: 3, md: 5 } }}>
        {/* Header Section - Compact */}
        <Box sx={{ textAlign: 'center', mb: { xs: 3, md: 4 } }}>
          <Avatar
            sx={{
              width: { xs: 60, md: 70 },
              height: { xs: 60, md: 70 },
              mx: 'auto',
              mb: 2,
              background: 'linear-gradient(135deg, #FF7700 0%, #E65100 100%)',
              boxShadow: '0 6px 24px rgba(255, 119, 0, 0.3)',
            }}
          >
            <DashboardIcon sx={{ fontSize: { xs: 30, md: 35 }, color: 'white' }} />
          </Avatar>
          
          <Typography
            variant={isMobile ? 'h4' : 'h3'}
            fontWeight={700}
            sx={{
              color: 'white',
              mb: 1,
              textShadow: '0 2px 10px rgba(0,0,0,0.3)',
            }}
          >
            TabTrips Admin Dashboard
          </Typography>
          
          <Typography
            variant="body1"
            sx={{
              color: 'rgba(255, 255, 255, 0.9)',
              maxWidth: 500,
              mx: 'auto',
              lineHeight: 1.5,
              fontSize: '1rem',
            }}
          >
            Comprehensive Hotel Management System
          </Typography>
        </Box>

        {/* Admin Panels Grid - Horizontal Row */}
        <Grid container spacing={2} sx={{ justifyContent: 'center' }}>
          {isAuthenticated && visiblePanels.length === 0 && (
            <Paper
              sx={{
                p: 4,
                mb: 3,
                textAlign: 'center',
                bgcolor: 'rgba(255,255,255,0.95)',
                borderRadius: 3,
              }}
            >
              <Typography variant="h6" fontWeight={700} color="text.primary" gutterBottom>
                No features assigned
              </Typography>
              <Typography color="text.secondary">
                Contact superadmin to get access to admin panels.
              </Typography>
            </Paper>
          )}
          {visiblePanels.map((panel, index) => (
            <Grid item xs={12} sm={6} md={4} lg={2.4} key={index}>
              <Card
                sx={{
                  height: '200px',
                  maxWidth: '280px',
                  mx: 'auto',
                  display: 'flex',
                  flexDirection: 'column',
                  background: 'rgba(255, 255, 255, 0.95)',
                  backdropFilter: 'blur(10px)',
                  border: '1px solid rgba(255, 255, 255, 0.2)',
                  transition: 'all 0.3s ease-in-out',
                  '&:hover': {
                    transform: 'translateY(-2px)',
                    boxShadow: '0 8px 16px rgba(0,0,0,0.1)',
                  },
                }}
              >
                <CardContent sx={{ p: 1.5, flex: 1 }}>
                  <Box
                    sx={{
                      width: 35,
                      height: 35,
                      borderRadius: 1.5,
                      background: panel.color,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      mb: 1.5,
                      mx: 'auto',
                      color: 'white',
                      boxShadow: '0 2px 8px rgba(0,0,0,0.2)',
                    }}
                  >
                    {React.cloneElement(panel.icon, { sx: { fontSize: 18 } })}
                  </Box>
                  
                  <Typography variant="subtitle1" fontWeight={600} sx={{ color: '#2E4057', mb: 1, textAlign: 'center', fontSize: '0.9rem' }}>
                    {panel.title}
                  </Typography>
                  
                  <Typography variant="caption" color="text.secondary" sx={{ textAlign: 'center', lineHeight: 1.3, fontSize: '0.75rem', display: 'block' }}>
                    {panel.description.length > 60 ? panel.description.substring(0, 60) + '...' : panel.description}
                  </Typography>
                </CardContent>
                
                <CardActions sx={{ p: 1.5, pt: 0 }}>
                  <Button
                    variant="contained"
                    fullWidth
                    size="small"
                    onClick={() => handleNavigate(panel.path)}
                    sx={{
                      background: panel.color,
                      color: 'white',
                      py: 0.75,
                      fontWeight: 600,
                      fontSize: '0.75rem',
                      boxShadow: '0 2px 8px rgba(0,0,0,0.2)',
                      '&:hover': {
                        background: panel.color,
                        boxShadow: '0 3px 12px rgba(0,0,0,0.3)',
                      },
                    }}
                  >
                    Access Panel
                  </Button>
                </CardActions>
              </Card>
            </Grid>
          ))}
        </Grid>

        {/* Footer - Compact */}
        <Box sx={{ textAlign: 'center', mt: 4 }}>
          <Typography
            variant="caption"
            sx={{
              color: 'rgba(255, 255, 255, 0.7)',
            }}
          >
            TabTrips Admin Dashboard v1.0 • Powered by React & Material-UI
          </Typography>
        </Box>
      </Container>
      
      {/* Add Room Type Dialog */}
      <Dialog 
        open={addRoomDialogOpen} 
        onClose={handleAddRoomClose}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 3,
            boxShadow: '0 8px 32px rgba(0, 0, 0, 0.12)'
          }
        }}
      >
        <DialogTitle sx={{ 
          textAlign: 'center', 
          py: 3,
          backgroundColor: '#fff5e6',
          borderBottom: '1px solid #e0e0e0'
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1 }}>
            <MeetingRoomIcon sx={{ color: '#FF7700', fontSize: 28 }} />
            <Typography variant="h5" fontWeight={600} sx={{ color: '#2E4057' }}>
              Add New Room Type
            </Typography>
          </Box>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            Add a new room category to the system
          </Typography>
        </DialogTitle>
        
        <DialogContent sx={{ py: 3, mt: 2 }}>
          <TextField
            fullWidth
            label="Room Name"
            value={roomName}
            onChange={(e) => setRoomName(e.target.value)}
            placeholder="Enter room type name (e.g., Deluxe, Standard, Suite)"
            variant="outlined"
            sx={{ 
              '& .MuiOutlinedInput-root': {
                borderRadius: 2,
                '&.Mui-focused fieldset': {
                  borderColor: '#FF7700',
                },
                '&:hover fieldset': {
                  borderColor: '#FF7700',
                },
              },
            }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <MeetingRoomIcon sx={{ color: '#FF7700' }} />
                </InputAdornment>
              ),
            }}
          />
        </DialogContent>
        
        <DialogActions sx={{ p: 3, gap: 1 }}>
          <Button
            onClick={handleAddRoomClose}
            variant="outlined"
            sx={{ borderRadius: 2 }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleAddRoomType}
            variant="contained"
            disabled={addRoomLoading || !roomName.trim()}
            sx={{ 
              backgroundColor: '#FF7700',
              '&:hover': { backgroundColor: '#e56900' },
              borderRadius: 2,
              px: 3
            }}
          >
            {addRoomLoading ? (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <CircularProgress size={16} sx={{ color: 'white' }} />
                Adding...
              </Box>
            ) : (
              'Add Room Type'
            )}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Room Type Dialog */}
      <Dialog 
        open={deleteRoomDialogOpen} 
        onClose={handleDeleteRoomClose}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 3,
            boxShadow: '0 8px 32px rgba(0, 0, 0, 0.12)'
          }
        }}
      >
        <DialogTitle sx={{ 
          textAlign: 'center', 
          py: 3,
          backgroundColor: '#ffebee',
          borderBottom: '1px solid #e0e0e0'
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1 }}>
            <DeleteIcon sx={{ color: '#f44336', fontSize: 28 }} />
            <Typography variant="h5" fontWeight={600} sx={{ color: '#2E4057' }}>
              Remove room category
            </Typography>
          </Box>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            Search by name and select a category to delete
          </Typography>
        </DialogTitle>
        
        <DialogContent sx={{ py: 3, mt: 2 }}>
          <Autocomplete
            fullWidth
            options={categorySearchResults}
            value={selectedCategory}
            onChange={(_, option) => {
              setSelectedCategory(option);
              setCategorySearchError('');
            }}
            inputValue={categorySearchInput}
            onInputChange={(_, value, reason) => {
              if (reason === 'input') {
                setCategorySearchInput(value);
                if (selectedCategory && value !== selectedCategory.category_name) {
                  setSelectedCategory(null);
                }
              } else if (reason === 'clear') {
                setCategorySearchInput('');
                setSelectedCategory(null);
              }
            }}
            getOptionLabel={(option) => option?.category_name || ''}
            isOptionEqualToValue={(option, value) => option?.category_id === value?.category_id}
            filterOptions={(x) => x}
            loading={categorySearchLoading}
            noOptionsText={
              categorySearchInput.trim()
                ? (categorySearchError || 'No matching categories')
                : 'Type to search room categories'
            }
            renderOption={(props, option) => (
              <Box component="li" {...props} key={option.category_id}>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%', gap: 1 }}>
                  <Box>
                    <Typography variant="body2" fontWeight={600}>{option.category_name}</Typography>
                    <Typography variant="caption" color="text.secondary">ID {option.category_id}</Typography>
                  </Box>
                  {option.hotels_using_count > 0 && (
                    <Chip
                      size="small"
                      label={`${option.hotels_using_count} hotel${option.hotels_using_count === 1 ? '' : 's'}`}
                      sx={{ height: 22, fontSize: '0.7rem' }}
                    />
                  )}
                </Box>
              </Box>
            )}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Search room category"
                placeholder="e.g. deluxe triple"
                variant="outlined"
                error={!!categorySearchError && !categorySearchLoading && categorySearchInput.trim() && !selectedCategory}
                helperText={
                  selectedCategory
                    ? `Selected: ${selectedCategory.category_name} (ID ${selectedCategory.category_id})`
                    : categorySearchError || 'Search by name — do not enter category IDs manually'
                }
                InputProps={{
                  ...params.InputProps,
                  startAdornment: (
                    <>
                      <InputAdornment position="start">
                        <SearchIcon sx={{ color: '#f44336' }} />
                      </InputAdornment>
                      {params.InputProps.startAdornment}
                    </>
                  ),
                  endAdornment: (
                    <>
                      {categorySearchLoading ? <CircularProgress color="inherit" size={18} /> : null}
                      {params.InputProps.endAdornment}
                    </>
                  ),
                }}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 2,
                    '&.Mui-focused fieldset': { borderColor: '#f44336' },
                    '&:hover fieldset': { borderColor: '#f44336' },
                  },
                }}
              />
            )}
          />
        </DialogContent>
        
        <DialogActions sx={{ p: 3, gap: 1 }}>
          <Button
            onClick={handleDeleteRoomClose}
            variant="outlined"
            sx={{ borderRadius: 2 }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleDeleteRoomType}
            variant="contained"
            disabled={deleteRoomLoading || !selectedCategory?.category_id}
            sx={{ 
              backgroundColor: '#f44336',
              '&:hover': { backgroundColor: '#d32f2f' },
              borderRadius: 2,
              px: 3
            }}
          >
            {deleteRoomLoading ? (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <CircularProgress size={16} sx={{ color: 'white' }} />
                Deleting...
              </Box>
            ) : (
              'Delete category'
            )}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Room category in use — force delete confirm */}
      <Dialog
        open={categoryInUseDialogOpen}
        onClose={() => !categoryForceDeleting && setCategoryInUseDialogOpen(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{ sx: { borderRadius: 3 } }}
      >
        <DialogTitle sx={{ fontWeight: 600, color: '#2E4057' }}>
          This room category is in use
        </DialogTitle>
        <DialogContent dividers>
          <DialogContentText sx={{ mb: 2 }}>
            {categoryInUseData?.message || (
              <>
                Removing <strong>{categoryInUseData?.category?.category_name || selectedCategory?.category_name}</strong> will also delete this room type from the hotels below, including inventory and rates for that room type.
              </>
            )}
          </DialogContentText>

          {(categoryInUseData?.hotels || []).length > 0 && (
            <Box sx={{ mb: 2, maxHeight: 220, overflow: 'auto', border: '1px solid #eee', borderRadius: 1, p: 1.5 }}>
              {(categoryInUseData.hotels || []).map((h) => (
                <Typography key={h.hotel_id} variant="body2" sx={{ py: 0.5 }}>
                  {h.hotel_name} <Typography component="span" variant="caption" color="text.secondary">({h.hotel_id})</Typography>
                </Typography>
              ))}
            </Box>
          )}

          {categoryPartialFailure && (
            <Alert severity="warning" sx={{ mb: 1 }}>
              <Typography variant="subtitle2" fontWeight={600} gutterBottom>Partial failure</Typography>
              {(categoryPartialFailure.errors || []).length > 0 && (
                <Box component="ul" sx={{ m: 0, pl: 2 }}>
                  {categoryPartialFailure.errors.map((err, i) => (
                    <li key={i}>
                      <Typography variant="caption">{typeof err === 'string' ? err : JSON.stringify(err)}</Typography>
                    </li>
                  ))}
                </Box>
              )}
              {(categoryPartialFailure.still_using || categoryPartialFailure.hotels || []).length > 0 && (
                <>
                  <Typography variant="caption" display="block" sx={{ mt: 1, fontWeight: 600 }}>Still in use at:</Typography>
                  {(categoryPartialFailure.still_using || categoryPartialFailure.hotels || []).map((h, i) => (
                    <Typography key={h.hotel_id ?? i} variant="caption" display="block">
                      {h.hotel_name || h.name} ({h.hotel_id})
                    </Typography>
                  ))}
                </>
              )}
            </Alert>
          )}
        </DialogContent>
        <DialogActions sx={{ p: 2, gap: 1 }}>
          <Button
            onClick={() => {
              setCategoryInUseDialogOpen(false);
              setCategoryPartialFailure(null);
            }}
            disabled={categoryForceDeleting}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            color="error"
            onClick={handleForceDeleteRoomCategory}
            disabled={categoryForceDeleting}
            sx={{ borderRadius: 2 }}
          >
            {categoryForceDeleting ? (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <CircularProgress size={16} color="inherit" />
                Deleting…
              </Box>
            ) : (
              'Yes, delete everywhere'
            )}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Cancel Booking Dialog */}
      <Dialog 
        open={cancelBookingDialogOpen} 
        onClose={handleCancelBookingClose}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 3,
            boxShadow: '0 8px 32px rgba(0, 0, 0, 0.12)',
            minWidth: '500px',
            maxWidth: '600px',
            mx: 'auto'
          }
        }}
      >
        <DialogTitle sx={{ 
          textAlign: 'center', 
          py: 3,
          backgroundColor: '#ffebee',
          borderBottom: '1px solid #e0e0e0'
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1 }}>
            <CancelIcon sx={{ color: '#ff9800', fontSize: 28 }} />
            <Typography variant="h5" fontWeight={600} sx={{ color: '#2E4057' }}>
              Booking Management
            </Typography>
          </Box>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            Cancel bookings or send warning notifications to hotels
          </Typography>
        </DialogTitle>
        
        <DialogContent sx={{ pt: 4, pb: 3, px: 3, overflow: 'visible' }}>
          <TextField
            fullWidth
            label="Booking ID"
            value={bookingId}
            onChange={(e) => setBookingId(e.target.value)}
            placeholder="Enter Booking ID (e.g., TT63459540)"
            variant="outlined"
            InputLabelProps={{ shrink: true }}
            sx={{
              '& .MuiOutlinedInput-root': {
                borderRadius: 2,
                '&.Mui-focused fieldset': { borderColor: '#ff9800' },
                '&:hover fieldset': { borderColor: '#ff9800' },
              },
              '& .MuiInputLabel-root.Mui-focused': { color: '#ff9800' },
            }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <ReceiptIcon sx={{ color: '#ff9800' }} />
                </InputAdornment>
              ),
            }}
          />
        </DialogContent>
        
        <DialogActions sx={{ p: 3, gap: 2, justifyContent: 'space-between' }}>
          <Button onClick={handleCancelBookingClose} variant="outlined" sx={{ borderRadius: 2, px: 3 }}>
            Close
          </Button>
          <Box sx={{ display: 'flex', gap: 2 }}>
            <Button
              onClick={handleSendWarning}
              variant="contained"
              disabled={cancelBookingLoading || !bookingId.trim()}
              sx={{ backgroundColor: '#ff9800', '&:hover': { backgroundColor: '#f57c00' }, borderRadius: 2, px: 3, minWidth: '140px' }}
            >
              {cancelBookingLoading ? (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <CircularProgress size={16} sx={{ color: 'white' }} />
                  Sending...
                </Box>
              ) : (
                'Send Warning'
              )}
            </Button>
            <Button
              onClick={handleCancelBooking}
              variant="contained"
              disabled={cancelBookingLoading || !bookingId.trim()}
              sx={{ backgroundColor: '#f44336', '&:hover': { backgroundColor: '#d32f2f' }, borderRadius: 2, px: 3, minWidth: '140px' }}
            >
              {cancelBookingLoading ? (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <CircularProgress size={16} sx={{ color: 'white' }} />
                  Canceling...
                </Box>
              ) : (
                'Cancel Booking'
              )}
            </Button>
          </Box>
        </DialogActions>
      </Dialog>

      {/* Cancel Booking Instant Refund Dialog */}
      <Dialog
        open={cancelInstantRefundDialogOpen}
        onClose={handleCancelInstantRefundClose}
        maxWidth="sm"
        fullWidth
        PaperProps={{ sx: { borderRadius: 3, boxShadow: '0 8px 32px rgba(0, 0, 0, 0.12)' } }}
      >
        <DialogTitle sx={{ textAlign: 'center', py: 3, backgroundColor: '#fff3e0', borderBottom: '1px solid #e0e0e0' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1 }}>
            <CancelIcon sx={{ color: '#f44336', fontSize: 28 }} />
            <Typography variant="h5" fontWeight={600} sx={{ color: '#2E4057' }}>
              Cancel Booking &amp; Instant Refund
            </Typography>
          </Box>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            This will cancel the booking and initiate instant refund.
          </Typography>
        </DialogTitle>
        <DialogContent sx={{ pt: 4, pb: 3, px: 3, overflow: 'visible' }}>
          <TextField
            fullWidth
            label="Booking ID"
            value={cancelInstantRefundBookingId}
            onChange={(e) => setCancelInstantRefundBookingId(e.target.value)}
            placeholder="Enter Booking ID (e.g., TT63459540)"
            variant="outlined"
            InputLabelProps={{ shrink: true }}
            sx={{
              '& .MuiOutlinedInput-root': {
                borderRadius: 2,
                '&.Mui-focused fieldset': { borderColor: '#f44336' },
                '&:hover fieldset': { borderColor: '#f44336' },
              },
              '& .MuiInputLabel-root.Mui-focused': { color: '#f44336' },
            }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <ReceiptIcon sx={{ color: '#f44336' }} />
                </InputAdornment>
              ),
            }}
          />
        </DialogContent>
        <DialogActions sx={{ p: 3, gap: 1, borderTop: '1px solid #eee' }}>
          <Button onClick={handleCancelInstantRefundClose} variant="outlined" sx={{ borderRadius: 2 }}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleCancelInstantRefundSubmit}
            disabled={cancelInstantRefundLoading || !cancelInstantRefundBookingId.trim()}
            sx={{ backgroundColor: '#f44336', '&:hover': { backgroundColor: '#d32f2f' }, borderRadius: 2, px: 3 }}
          >
            {cancelInstantRefundLoading ? (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <CircularProgress size={16} sx={{ color: 'white' }} />
                Processing...
              </Box>
            ) : (
              'Cancel &amp; Refund'
            )}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Compensation & Credit Note Dialog */}
      <Dialog 
        open={compensationDialogOpen} 
        onClose={handleCompensationClose}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 3,
            boxShadow: '0 8px 32px rgba(0, 0, 0, 0.12)'
          }
        }}
      >
        <DialogTitle sx={{ 
          textAlign: 'center', 
          py: 3,
          backgroundColor: '#f3e5f5',
          borderBottom: '1px solid #e0e0e0'
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1 }}>
            <ReceiptIcon sx={{ color: '#9c27b0', fontSize: 28 }} />
            <Typography variant="h5" fontWeight={600} sx={{ color: '#2E4057' }}>
              Compensation & Credit Note
            </Typography>
          </Box>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            Add compensation and credit note for a hotel
          </Typography>
        </DialogTitle>
        
        <DialogContent sx={{ py: 4, px: 3, pt: 3 }}>
          <Box sx={{ mb: 3 }}>
            <TextField
              fullWidth
              label="Hotel ID"
              value={compensationHotelId}
              onChange={(e) => setCompensationHotelId(e.target.value)}
              placeholder="Enter Hotel ID (e.g., 50050011)"
              variant="outlined"
              sx={{ 
                '& .MuiOutlinedInput-root': {
                  borderRadius: 2,
                  '&.Mui-focused fieldset': {
                    borderColor: '#9c27b0',
                  },
                  '&:hover fieldset': {
                    borderColor: '#9c27b0',
                  },
                },
                '& .MuiInputLabel-root': {
                  transformOrigin: 'top left',
                  '&.Mui-focused': {
                    color: '#9c27b0',
                  },
                },
                '& .MuiInputLabel-shrink': {
                  transformOrigin: 'top left',
                },
              }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <BusinessIcon sx={{ color: '#9c27b0' }} />
                  </InputAdornment>
                ),
              }}
            />
          </Box>
          
          <Box sx={{ mb: 3 }}>
            <TextField
              fullWidth
              label="Month & Year"
              type="month"
              value={compensationMonth}
              onChange={(e) => setCompensationMonth(e.target.value)}
              placeholder="Select month and year"
              variant="outlined"
              inputProps={{
                max: getCurrentMonth()
              }}
              InputLabelProps={{
                shrink: true
              }}
              sx={{ 
                '& .MuiOutlinedInput-root': {
                  borderRadius: 2,
                  '&.Mui-focused fieldset': {
                    borderColor: '#9c27b0',
                  },
                  '&:hover fieldset': {
                    borderColor: '#9c27b0',
                  },
                },
                '& .MuiInputLabel-root': {
                  transformOrigin: 'top left',
                  '&.Mui-focused': {
                    color: '#9c27b0',
                  },
                },
                '& .MuiInputLabel-shrink': {
                  transformOrigin: 'top left',
                },
              }}
              helperText="Cannot select future months"
            />
          </Box>
          
          <Box sx={{ mb: 2 }}>
            <TextField
              fullWidth
              label="Amount"
              type="number"
              value={compensationAmount}
              onChange={(e) => setCompensationAmount(e.target.value)}
              placeholder="Enter amount (e.g., 100)"
              variant="outlined"
              inputProps={{
                min: 0,
                step: 0.01
              }}
              sx={{ 
                '& .MuiOutlinedInput-root': {
                  borderRadius: 2,
                  '&.Mui-focused fieldset': {
                    borderColor: '#9c27b0',
                  },
                  '&:hover fieldset': {
                    borderColor: '#9c27b0',
                  },
                },
                '& .MuiInputLabel-root': {
                  transformOrigin: 'top left',
                  '&.Mui-focused': {
                    color: '#9c27b0',
                  },
                },
                '& .MuiInputLabel-shrink': {
                  transformOrigin: 'top left',
                },
              }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Typography sx={{ color: '#9c27b0', fontWeight: 600 }}>₹</Typography>
                  </InputAdornment>
                ),
              }}
            />
          </Box>
        </DialogContent>
        
        <DialogActions sx={{ p: 3, gap: 1 }}>
          <Button
            onClick={handleCompensationClose}
            variant="outlined"
            sx={{ borderRadius: 2 }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleCompensationSubmit}
            variant="contained"
            disabled={compensationLoading || !compensationHotelId.trim() || !compensationMonth.trim() || !compensationAmount.trim()}
            sx={{ 
              backgroundColor: '#9c27b0',
              '&:hover': { backgroundColor: '#7b1fa2' },
              borderRadius: 2,
              px: 3
            }}
          >
            {compensationLoading ? (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <CircularProgress size={16} sx={{ color: 'white' }} />
                Submitting...
              </Box>
            ) : (
              'Submit'
            )}
          </Button>
        </DialogActions>
      </Dialog>
      
      {/* Update Linked Account Dialog */}
      <Dialog 
        open={updateLinkedAccountDialogOpen} 
        onClose={handleUpdateLinkedAccountClose}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 3,
            boxShadow: '0 8px 32px rgba(0, 0, 0, 0.12)'
          }
        }}
      >
        <DialogTitle sx={{ 
          textAlign: 'center', 
          py: 3,
          backgroundColor: '#e3f2fd',
          borderBottom: '1px solid #e0e0e0'
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1 }}>
            <LinkIcon sx={{ color: '#2196f3', fontSize: 28 }} />
            <Typography variant="h5" fontWeight={600} sx={{ color: '#2E4057' }}>
              Update Linked Account
            </Typography>
          </Box>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            Link account details to payment gateway
          </Typography>
        </DialogTitle>
        
        <DialogContent sx={{ py: 4, px: 3, pt: 3 }}>
          {/* Information Box */}
          <Box 
            sx={{ 
              mb: 4,
              p: 3,
              borderRadius: 2,
              backgroundColor: '#f5f5f5',
              border: '1px solid #e0e0e0',
              borderLeft: '4px solid #2196f3'
            }}
          >
            <Typography 
              variant="body1" 
              sx={{ 
                color: '#2E4057',
                fontWeight: 500,
                lineHeight: 1.6,
                textAlign: 'center'
              }}
            >
              All account details will be linked to the payment gateway for this hotel.
            </Typography>
          </Box>

          {/* Hotel ID Input */}
          <Box sx={{ mb: 2 }}>
            <TextField
              fullWidth
              label="Hotel ID"
              value={linkedAccountHotelId}
              onChange={(e) => setLinkedAccountHotelId(e.target.value)}
              placeholder="Enter Hotel ID (e.g., 50050011)"
              variant="outlined"
              sx={{ 
                '& .MuiOutlinedInput-root': {
                  borderRadius: 2,
                  '&.Mui-focused fieldset': {
                    borderColor: '#2196f3',
                  },
                  '&:hover fieldset': {
                    borderColor: '#2196f3',
                  },
                },
                '& .MuiInputLabel-root': {
                  transformOrigin: 'top left',
                  '&.Mui-focused': {
                    color: '#2196f3',
                  },
                },
                '& .MuiInputLabel-shrink': {
                  transformOrigin: 'top left',
                },
              }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <BusinessIcon sx={{ color: '#2196f3' }} />
                  </InputAdornment>
                ),
              }}
            />
          </Box>
        </DialogContent>
        
        <DialogActions sx={{ p: 3, gap: 1 }}>
          <Button
            onClick={handleUpdateLinkedAccountClose}
            variant="outlined"
            sx={{ borderRadius: 2 }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleUpdateLinkedAccountSubmit}
            variant="contained"
            disabled={updateLinkedAccountLoading || !linkedAccountHotelId.trim()}
            sx={{ 
              backgroundColor: '#2196f3',
              '&:hover': { backgroundColor: '#1976d2' },
              borderRadius: 2,
              px: 3
            }}
          >
            {updateLinkedAccountLoading ? (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <CircularProgress size={16} sx={{ color: 'white' }} />
                Updating...
              </Box>
            ) : (
              'Submit'
            )}
          </Button>
        </DialogActions>
      </Dialog>
      
      {/* Map TabTrips with StayFlexi Dialog */}
      <Dialog 
        open={mapTabtripsStayflexiDialogOpen} 
        onClose={handleMapTabtripsStayflexiClose}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 3,
            boxShadow: '0 8px 32px rgba(0, 0, 0, 0.12)'
          }
        }}
      >
        <DialogTitle sx={{ 
          textAlign: 'center', 
          py: 3,
          backgroundColor: '#e8f5e8',
          borderBottom: '1px solid #e0e0e0'
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1 }}>
            <MapIcon sx={{ color: '#4caf50', fontSize: 28 }} />
            <Typography variant="h5" fontWeight={600} sx={{ color: '#2E4057' }}>
              Map TabTrips with StayFlexi
            </Typography>
          </Box>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            Create mapping between TabTrips and StayFlexi hotel IDs
          </Typography>
        </DialogTitle>
        
        <DialogContent sx={{ py: 4, px: 3, pt: 3 }}>
          <Box sx={{ mb: 3 }}>
            <TextField
              fullWidth
              label="TabTrips ID"
              value={tabtripsId}
              onChange={(e) => setTabtripsId(e.target.value)}
              placeholder="Enter TabTrips Hotel ID (e.g., 50050011)"
              variant="outlined"
              sx={{ 
                '& .MuiOutlinedInput-root': {
                  borderRadius: 2,
                  '&.Mui-focused fieldset': {
                    borderColor: '#4caf50',
                  },
                  '&:hover fieldset': {
                    borderColor: '#4caf50',
                  },
                },
                '& .MuiInputLabel-root': {
                  transformOrigin: 'top left',
                  '&.Mui-focused': {
                    color: '#4caf50',
                  },
                },
                '& .MuiInputLabel-shrink': {
                  transformOrigin: 'top left',
                },
              }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <BusinessIcon sx={{ color: '#4caf50' }} />
                  </InputAdornment>
                ),
              }}
            />
          </Box>
          
          <Box sx={{ mb: 3 }}>
            <TextField
              fullWidth
              label="StayFlexi ID"
              value={stayFlexiId}
              onChange={(e) => setStayFlexiId(e.target.value)}
              placeholder="Enter StayFlexi Hotel ID (e.g., 31773)"
              variant="outlined"
              sx={{ 
                '& .MuiOutlinedInput-root': {
                  borderRadius: 2,
                  '&.Mui-focused fieldset': {
                    borderColor: '#4caf50',
                  },
                  '&:hover fieldset': {
                    borderColor: '#4caf50',
                  },
                },
                '& .MuiInputLabel-root': {
                  transformOrigin: 'top left',
                  '&.Mui-focused': {
                    color: '#4caf50',
                  },
                },
                '& .MuiInputLabel-shrink': {
                  transformOrigin: 'top left',
                },
              }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <BusinessIcon sx={{ color: '#4caf50' }} />
                  </InputAdornment>
                ),
              }}
            />
          </Box>
          
          <Box sx={{ mb: 2 }}>
            <FormControl fullWidth>
              <InputLabel>Use StayFlexi</InputLabel>
              <Select
                value={isUseStayflexi}
                label="Use StayFlexi"
                onChange={(e) => setIsUseStayflexi(e.target.value)}
                sx={{ 
                  borderRadius: 2,
                  '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                    borderColor: '#4caf50',
                  },
                  '&:hover .MuiOutlinedInput-notchedOutline': {
                    borderColor: '#4caf50',
                  },
                }}
              >
                <MenuItem value="no">No</MenuItem>
                <MenuItem value="yes">Yes</MenuItem>
              </Select>
            </FormControl>
          </Box>
        </DialogContent>
        
        <DialogActions sx={{ p: 3, gap: 1 }}>
          <Button
            onClick={handleMapTabtripsStayflexiClose}
            variant="outlined"
            sx={{ borderRadius: 2 }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleMapTabtripsStayflexiSubmit}
            variant="contained"
            disabled={mapTabtripsStayflexiLoading || !tabtripsId.trim() || !stayFlexiId.trim()}
            sx={{ 
              backgroundColor: '#4caf50',
              '&:hover': { backgroundColor: '#388e3c' },
              borderRadius: 2,
              px: 3
            }}
          >
            {mapTabtripsStayflexiLoading ? (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <CircularProgress size={16} sx={{ color: 'white' }} />
                Mapping...
              </Box>
            ) : (
              'Map'
            )}
          </Button>
        </DialogActions>
      </Dialog>
      
      {/* Generate Voucher Dialog */}
      <Dialog 
        open={generateVoucherDialogOpen} 
        onClose={handleGenerateVoucherClose}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 3,
            boxShadow: '0 8px 32px rgba(0, 0, 0, 0.12)'
          }
        }}
      >
        <DialogTitle sx={{ 
          textAlign: 'center', 
          py: 3,
          backgroundColor: '#f3e5f5',
          borderBottom: '1px solid #e0e0e0'
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1 }}>
            <DescriptionIcon sx={{ color: '#9c27b0', fontSize: 28 }} />
            <Typography variant="h5" fontWeight={600} sx={{ color: '#2E4057' }}>
              Generate Voucher
            </Typography>
          </Box>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            Generate and download vouchers for bookings
          </Typography>
        </DialogTitle>
        
        <DialogContent sx={{ py: 4, px: 3 }}>
          {/* Booking ID Input */}
          <Box sx={{ mb: 4 }}>
            <TextField
              fullWidth
              label="Booking ID"
              value={voucherBookingId}
              onChange={(e) => setVoucherBookingId(e.target.value)}
              placeholder="Enter Booking ID (e.g., 1100)"
              variant="outlined"
              sx={{ 
                '& .MuiOutlinedInput-root': {
                  borderRadius: 2,
                  '&.Mui-focused fieldset': {
                    borderColor: '#9c27b0',
                  },
                  '&:hover fieldset': {
                    borderColor: '#9c27b0',
                  },
                },
                '& .MuiInputLabel-root': {
                  transformOrigin: 'top left',
                  '&.Mui-focused': {
                    color: '#9c27b0',
                  },
                },
              }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <ReceiptIcon sx={{ color: '#9c27b0' }} />
                  </InputAdornment>
                ),
              }}
            />
          </Box>

          {/* Voucher Cards */}
          <Grid container spacing={3}>
            {/* Guest Voucher Card */}
            <Grid item xs={12} md={6}>
              <Card sx={{ 
                p: 3,
                border: '2px solid #e0e0e0',
                borderRadius: 2,
                transition: 'all 0.3s ease',
                '&:hover': {
                  borderColor: '#4caf50',
                  boxShadow: '0 4px 12px rgba(76, 175, 80, 0.2)'
                }
              }}>
                <Box sx={{ textAlign: 'center', mb: 2 }}>
                  <DescriptionIcon sx={{ fontSize: 48, color: '#4caf50', mb: 1 }} />
                  <Typography variant="h6" fontWeight={600} sx={{ color: '#2E4057', mb: 1 }}>
                    Guest Voucher
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Generate voucher for guest
                  </Typography>
                </Box>
                <Button
                  variant="contained"
                  fullWidth
                  onClick={() => handleGenerateVoucher('guest')}
                  disabled={generatingGuestVoucher || generatingHotelierVoucher || !voucherBookingId.trim()}
                  startIcon={generatingGuestVoucher ? <CircularProgress size={16} sx={{ color: 'white' }} /> : <DescriptionIcon />}
                  sx={{
                    backgroundColor: '#4caf50',
                    '&:hover': { backgroundColor: '#388e3c' },
                    borderRadius: 2,
                    py: 1.5
                  }}
                >
                  {generatingGuestVoucher ? 'Generating...' : 'Generate/Download Guest Voucher'}
                </Button>
              </Card>
            </Grid>

            {/* Hotelier Voucher Card */}
            <Grid item xs={12} md={6}>
              <Card sx={{ 
                p: 3,
                border: '2px solid #e0e0e0',
                borderRadius: 2,
                transition: 'all 0.3s ease',
                '&:hover': {
                  borderColor: '#2196f3',
                  boxShadow: '0 4px 12px rgba(33, 150, 243, 0.2)'
                }
              }}>
                <Box sx={{ textAlign: 'center', mb: 2 }}>
                  <DescriptionIcon sx={{ fontSize: 48, color: '#2196f3', mb: 1 }} />
                  <Typography variant="h6" fontWeight={600} sx={{ color: '#2E4057', mb: 1 }}>
                    Hotelier Voucher
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Generate voucher for hotelier
                  </Typography>
                </Box>
                <Button
                  variant="contained"
                  fullWidth
                  onClick={() => handleGenerateVoucher('hotelier')}
                  disabled={generatingGuestVoucher || generatingHotelierVoucher || !voucherBookingId.trim()}
                  startIcon={generatingHotelierVoucher ? <CircularProgress size={16} sx={{ color: 'white' }} /> : <DescriptionIcon />}
                  sx={{
                    backgroundColor: '#2196f3',
                    '&:hover': { backgroundColor: '#1976d2' },
                    borderRadius: 2,
                    py: 1.5
                  }}
                >
                  {generatingHotelierVoucher ? 'Generating...' : 'Generate/Download Hotelier Voucher'}
                </Button>
              </Card>
            </Grid>
          </Grid>
        </DialogContent>
        
        <DialogActions sx={{ p: 3, gap: 1 }}>
          <Button
            onClick={handleGenerateVoucherClose}
            variant="outlined"
            disabled={generatingGuestVoucher || generatingHotelierVoucher}
            sx={{ borderRadius: 2 }}
          >
            Close
          </Button>
        </DialogActions>
      </Dialog>
      
      {/* Settle Invoice Dialog */}
      <Dialog
        open={settleInvoiceDialogOpen}
        onClose={handleSettleInvoiceClose}
        maxWidth="sm"
        fullWidth
        PaperProps={{ sx: { borderRadius: 3, boxShadow: '0 8px 32px rgba(0, 0, 0, 0.12)' } }}
      >
        <DialogTitle
          sx={{
            textAlign: 'center',
            py: 3,
            backgroundColor: '#e8f5e9',
            borderBottom: '1px solid #c8e6c9'
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1 }}>
            <ReceiptIcon sx={{ color: '#2e7d32', fontSize: 28 }} />
            <Typography variant="h5" fontWeight={600} sx={{ color: '#1b5e20' }}>
              Settle Invoice
            </Typography>
          </Box>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            Mark hotel invoice as settled for a specific month
          </Typography>
        </DialogTitle>
        <DialogContent sx={{ pt: 5, pb: 3, px: 3, overflow: 'visible' }}>
          <TextField
            fullWidth
            label="Hotel ID"
            type="number"
            value={settleInvoiceHotelId}
            onChange={(e) => setSettleInvoiceHotelId(e.target.value)}
            placeholder="e.g. 123"
            variant="outlined"
            InputLabelProps={{ shrink: true }}
            sx={{ mb: 3 }}
            inputProps={{ min: 1, step: 1 }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <BusinessIcon sx={{ color: '#2e7d32' }} />
                </InputAdornment>
              )
            }}
          />
          <TextField
            fullWidth
            label="Month & Year"
            type="month"
            value={settleInvoiceMonth}
            onChange={(e) => setSettleInvoiceMonth(e.target.value)}
            variant="outlined"
            inputProps={{ max: getCurrentMonth() }}
            InputLabelProps={{ shrink: true }}
            helperText="Future months are not allowed"
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <CalendarIcon sx={{ color: '#2e7d32' }} />
                </InputAdornment>
              )
            }}
          />
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2, borderTop: '1px solid #eee', gap: 1 }}>
          <Button onClick={handleSettleInvoiceClose} variant="outlined" sx={{ borderRadius: 2 }}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleSettleInvoiceSubmit}
            disabled={settleInvoiceLoading || !settleInvoiceHotelId.trim() || !settleInvoiceMonth}
            sx={{
              backgroundColor: '#2e7d32',
              '&:hover': { backgroundColor: '#1b5e20' },
              borderRadius: 2,
              px: 3
            }}
          >
            {settleInvoiceLoading ? (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <CircularProgress size={16} sx={{ color: 'white' }} />
                Submitting...
              </Box>
            ) : (
              'Mark as Settled'
            )}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Set Nearby Places Dialog */}
      <Dialog
        open={nearbyPlacesDialogOpen}
        onClose={handleNearbyPlacesClose}
        maxWidth="sm"
        fullWidth
        PaperProps={{ sx: { borderRadius: 3, boxShadow: '0 8px 32px rgba(0, 0, 0, 0.12)' } }}
      >
        <DialogTitle
          sx={{
            textAlign: 'center',
            py: 3,
            backgroundColor: '#e8f5e9',
            borderBottom: '1px solid #c8e6c9'
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1 }}>
            <MapIcon sx={{ color: '#2e7d32', fontSize: 28 }} />
            <Typography variant="h5" fontWeight={600} sx={{ color: '#1b5e20' }}>
              Set Nearby Places
            </Typography>
          </Box>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            Enter a hotel ID to trigger nearby places setup.
          </Typography>
        </DialogTitle>
        <DialogContent sx={{ pt: 5, pb: 3, px: 3, overflow: 'visible' }}>
          <TextField
            fullWidth
            label="Hotel ID"
            type="number"
            value={nearbyPlacesHotelId}
            onChange={(e) => setNearbyPlacesHotelId(e.target.value)}
            placeholder="e.g. 50050219"
            variant="outlined"
            InputLabelProps={{ shrink: true }}
            inputProps={{ min: 1, step: 1 }}
            helperText="Calls action=cronSetHotelNearByPlaces for this hotel"
            sx={{
              '& .MuiOutlinedInput-root': {
                borderRadius: 2,
                '&.Mui-focused fieldset': { borderColor: '#2e7d32' },
                '&:hover fieldset': { borderColor: '#2e7d32' }
              },
              '& .MuiInputLabel-root.Mui-focused': { color: '#2e7d32' }
            }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <BusinessIcon sx={{ color: '#2e7d32' }} />
                </InputAdornment>
              )
            }}
          />
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2, borderTop: '1px solid #eee', gap: 1 }}>
          <Button onClick={handleNearbyPlacesClose} variant="outlined" disabled={nearbyPlacesLoading}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleNearbyPlacesSubmit}
            disabled={nearbyPlacesLoading || !nearbyPlacesHotelId.trim()}
            sx={{ backgroundColor: '#2e7d32', '&:hover': { backgroundColor: '#1b5e20' } }}
          >
            {nearbyPlacesLoading ? (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <CircularProgress size={16} sx={{ color: 'white' }} />
                Processing...
              </Box>
            ) : (
              'Set Nearby Places'
            )}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Stayflexi Onboarding Dialog */}
      <Dialog
        open={stayflexiOnboardingDialogOpen}
        onClose={handleStayflexiOnboardingClose}
        maxWidth="sm"
        fullWidth
        PaperProps={{ sx: { borderRadius: 3, boxShadow: '0 8px 32px rgba(0, 0, 0, 0.12)' } }}
      >
        <DialogTitle
          sx={{
            textAlign: 'center',
            py: 3,
            backgroundColor: '#e3f2fd',
            borderBottom: '1px solid #90caf9',
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1 }}>
            <LinkIcon sx={{ color: '#1565c0', fontSize: 28 }} />
            <Typography variant="h5" fontWeight={600} sx={{ color: '#0d47a1' }}>
              Stayflexi Onboarding
            </Typography>
          </Box>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            Enter a TabTrips hotel ID to onboard on Stayflexi.
          </Typography>
        </DialogTitle>
        <DialogContent sx={{ pt: 5, pb: 3, px: 3, overflow: 'visible' }}>
          <TextField
            fullWidth
            label="Hotel ID"
            type="number"
            value={stayflexiOnboardingHotelId}
            onChange={(e) => setStayflexiOnboardingHotelId(e.target.value)}
            placeholder="e.g. 50050011"
            variant="outlined"
            InputLabelProps={{ shrink: true }}
            inputProps={{ min: 1, step: 1 }}
            sx={{
              '& .MuiOutlinedInput-root': {
                borderRadius: 2,
                '&.Mui-focused fieldset': { borderColor: '#1565c0' },
                '&:hover fieldset': { borderColor: '#1565c0' },
              },
              '& .MuiInputLabel-root.Mui-focused': { color: '#1565c0' },
            }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <BusinessIcon sx={{ color: '#1565c0' }} />
                </InputAdornment>
              ),
            }}
          />
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2, borderTop: '1px solid #eee', gap: 1 }}>
          <Button onClick={handleStayflexiOnboardingClose} variant="outlined" disabled={stayflexiOnboardingLoading}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleStayflexiOnboardingSubmit}
            disabled={stayflexiOnboardingLoading || !stayflexiOnboardingHotelId.trim()}
            sx={{ backgroundColor: '#1565c0', '&:hover': { backgroundColor: '#0d47a1' } }}
          >
            {stayflexiOnboardingLoading ? (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <CircularProgress size={16} sx={{ color: 'white' }} />
                Submitting...
              </Box>
            ) : (
              'Submit'
            )}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Stayflexi Mapping Token Dialog */}
      <Dialog
        open={stayflexiMappingTokenDialogOpen}
        onClose={handleStayflexiMappingTokenClose}
        maxWidth="sm"
        fullWidth
        PaperProps={{ sx: { borderRadius: 3, boxShadow: '0 8px 32px rgba(0, 0, 0, 0.12)' } }}
      >
        <DialogTitle
          sx={{
            textAlign: 'center',
            py: 3,
            backgroundColor: '#e0f7fa',
            borderBottom: '1px solid #b2ebf2'
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1 }}>
            <SecurityIcon sx={{ color: '#00838f', fontSize: 28 }} />
            <Typography variant="h5" fontWeight={600} sx={{ color: '#006064' }}>
              Stayflexi Mapping Token
            </Typography>
          </Box>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            Paste the JWT token to update StayFlexi channel config.
          </Typography>
        </DialogTitle>
        <DialogContent sx={{ pt: 5, pb: 3, px: 3, overflow: 'visible' }}>
          <TextField
            fullWidth
            label="Token"
            value={stayflexiMappingToken}
            onChange={(e) => setStayflexiMappingToken(e.target.value)}
            placeholder="Paste JWT token here"
            variant="outlined"
            multiline
            minRows={3}
            maxRows={8}
            InputLabelProps={{ shrink: true }}
            helperText="Calls action=setStayflexiChannelConfigToken"
            sx={{
              '& .MuiOutlinedInput-root': {
                borderRadius: 2,
                '&.Mui-focused fieldset': { borderColor: '#00838f' },
                '&:hover fieldset': { borderColor: '#00838f' }
              },
              '& .MuiInputLabel-root.Mui-focused': { color: '#00838f' }
            }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start" sx={{ alignSelf: 'flex-start', mt: 1.5 }}>
                  <LinkIcon sx={{ color: '#00838f' }} />
                </InputAdornment>
              )
            }}
          />
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2, borderTop: '1px solid #eee', gap: 1 }}>
          <Button onClick={handleStayflexiMappingTokenClose} variant="outlined" disabled={stayflexiMappingTokenLoading}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleStayflexiMappingTokenSubmit}
            disabled={stayflexiMappingTokenLoading || !stayflexiMappingToken.trim()}
            sx={{ backgroundColor: '#00838f', '&:hover': { backgroundColor: '#006064' } }}
          >
            {stayflexiMappingTokenLoading ? (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <CircularProgress size={16} sx={{ color: 'white' }} />
                Updating...
              </Box>
            ) : (
              'Change'
            )}
          </Button>
        </DialogActions>
      </Dialog>

      {/* AI Pricing Discount Dialog */}
      <Dialog
        open={aiPricingDiscountDialogOpen}
        onClose={handleAiPricingDiscountClose}
        maxWidth="sm"
        fullWidth
        PaperProps={{ sx: { borderRadius: 3, boxShadow: '0 8px 32px rgba(0, 0, 0, 0.12)' } }}
      >
        <DialogTitle
          sx={{
            textAlign: 'center',
            py: 3,
            backgroundColor: '#f3e5f5',
            borderBottom: '1px solid #e1bee7'
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1 }}>
            <DiscountIcon sx={{ color: '#6a1b9a', fontSize: 28 }} />
            <Typography variant="h5" fontWeight={600} sx={{ color: '#4a148c' }}>
              AI Pricing Discount
            </Typography>
          </Box>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            Set global undercut discount in INR for maps AI pricing.
          </Typography>
        </DialogTitle>
        <DialogContent sx={{ pt: 5, pb: 3, px: 3, overflow: 'visible' }}>
          <TextField
            fullWidth
            label="Discount Amount (INR)"
            type="number"
            value={aiPricingDiscountInr}
            onChange={(e) => setAiPricingDiscountInr(e.target.value)}
            placeholder="e.g. 50"
            variant="outlined"
            InputLabelProps={{ shrink: true }}
            inputProps={{ min: 0, step: 1 }}
            helperText="This value will be sent as { inr: <amount> }"
            sx={{
              '& .MuiOutlinedInput-root': {
                borderRadius: 2,
                '&.Mui-focused fieldset': { borderColor: '#6a1b9a' },
                '&:hover fieldset': { borderColor: '#6a1b9a' }
              },
              '& .MuiInputLabel-root.Mui-focused': { color: '#6a1b9a' }
            }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Typography sx={{ color: '#6a1b9a', fontWeight: 600 }}>₹</Typography>
                </InputAdornment>
              )
            }}
          />
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2, borderTop: '1px solid #eee', gap: 1 }}>
          <Button onClick={handleAiPricingDiscountClose} variant="outlined" disabled={aiPricingDiscountLoading}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleAiPricingDiscountSubmit}
            disabled={aiPricingDiscountLoading || !aiPricingDiscountInr.trim()}
            sx={{ backgroundColor: '#6a1b9a', '&:hover': { backgroundColor: '#4a148c' } }}
          >
            {aiPricingDiscountLoading ? (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <CircularProgress size={16} sx={{ color: 'white' }} />
                Saving...
              </Box>
            ) : (
              'Save Discount'
            )}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Hotel Room Type Dialog */}
      <Dialog
        open={deleteRoomTypeDialogOpen}
        onClose={handleDeleteRoomTypeDialogClose}
        maxWidth="sm"
        fullWidth
        PaperProps={{ sx: { borderRadius: 3, boxShadow: '0 8px 32px rgba(0,0,0,0.12)' } }}
      >
        <DialogTitle sx={{ textAlign: 'center', py: 2.5, backgroundColor: '#ffebee', borderBottom: '1px solid #ffcdd2' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1 }}>
            <MeetingRoomIcon sx={{ color: '#c62828', fontSize: 28 }} />
            <Typography variant="h5" fontWeight={600} sx={{ color: '#b71c1c' }}>
              Delete Hotel Room Type
            </Typography>
          </Box>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            Load room types for a hotel, then delete one. This removes all rates and data for that room type.
          </Typography>
        </DialogTitle>
        <DialogContent sx={{ pt: 4, pb: 2, px: 3, overflow: 'visible' }}>
          <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-start', flexWrap: 'wrap', mb: 3 }}>
            <TextField
              label="Hotel ID"
              type="number"
              value={deleteRoomTypeHotelId}
              onChange={(e) => setDeleteRoomTypeHotelId(e.target.value)}
              placeholder="e.g. 50050001"
              variant="outlined"
              size="small"
              InputLabelProps={{ shrink: true }}
              sx={{ minWidth: 160 }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <BusinessIcon sx={{ color: '#c62828', fontSize: 20 }} />
                  </InputAdornment>
                ),
              }}
            />
            <Button
              variant="contained"
              onClick={handleLoadRoomTypes}
              disabled={roomTypesLoading || !deleteRoomTypeHotelId.trim()}
              sx={{ backgroundColor: '#FF7700', '&:hover': { backgroundColor: '#e65100' }, minWidth: 120 }}
            >
              {roomTypesLoading ? <CircularProgress size={20} sx={{ color: 'white' }} /> : 'Load Room Types'}
            </Button>
          </Box>
          {roomTypesHotelName && (
            <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 2 }}>
              {roomTypesList.length ? `${roomTypesHotelName} – ${roomTypesList.length} room type(s)` : `${roomTypesHotelName} – No room types`}
            </Typography>
          )}
          {roomTypesList.length > 0 ? (
            <TableContainer component={Paper} variant="outlined" sx={{ borderRadius: 2, border: '1px solid #e0e0e0' }}>
              <Table size="small">
                <TableHead>
                  <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
                    <TableCell sx={{ fontWeight: 700, color: '#2E4057' }}>ID</TableCell>
                    <TableCell sx={{ fontWeight: 700, color: '#2E4057' }}>Room Type Name</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 700, color: '#2E4057' }}>Action</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {roomTypesList.map((rt) => (
                    <TableRow key={rt.roomtype_id} hover>
                      <TableCell>{rt.roomtype_id}</TableCell>
                      <TableCell>{rt.roomtype_name}</TableCell>
                      <TableCell align="right">
                        <Button
                          size="small"
                          variant="outlined"
                          color="error"
                          startIcon={<DeleteIcon />}
                          onClick={() => setDeleteRoomTypeConfirm({
                            hotel_id: deleteRoomTypeHotelId.trim(),
                            roomtype_id: rt.roomtype_id,
                            roomtype_name: rt.roomtype_name
                          })}
                        >
                          Delete
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          ) : roomTypesHotelName ? (
            <Typography variant="body2" color="text.secondary" sx={{ py: 2 }}>
              No room types to show. Try another hotel.
            </Typography>
          ) : null}
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2, borderTop: '1px solid #eee' }}>
          <Button onClick={handleDeleteRoomTypeDialogClose} variant="outlined" sx={{ borderRadius: 2 }}>
            Close
          </Button>
        </DialogActions>
      </Dialog>

      {/* Confirm Delete Room Type Dialog */}
      <Dialog
        open={!!deleteRoomTypeConfirm}
        onClose={() => setDeleteRoomTypeConfirm(null)}
        maxWidth="xs"
        fullWidth
        PaperProps={{ sx: { borderRadius: 3 } }}
      >
        <DialogTitle sx={{ color: '#c62828', fontWeight: 600 }}>
          Delete room type?
        </DialogTitle>
        <DialogContent>
          <DialogContentText>
            {deleteRoomTypeConfirm && (
              <>
                Delete room type &quot;{deleteRoomTypeConfirm.roomtype_name}&quot;? This will remove all rates and data for this room type. This action cannot be undone.
              </>
            )}
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2, gap: 1 }}>
          <Button onClick={() => setDeleteRoomTypeConfirm(null)} variant="outlined">
            Cancel
          </Button>
          <Button
            variant="contained"
            color="error"
            onClick={handleDeleteRoomTypeConfirmSubmit}
            disabled={deleteRoomTypeDeleting}
            sx={{ '&:hover': { backgroundColor: '#b71c1c' } }}
          >
            {deleteRoomTypeDeleting ? <CircularProgress size={20} sx={{ color: 'white' }} /> : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Add Rating Dialog */}
      <Dialog
        open={addRatingDialogOpen}
        onClose={handleAddRatingClose}
        maxWidth="sm"
        fullWidth
        PaperProps={{ sx: { borderRadius: 3, boxShadow: '0 8px 32px rgba(0, 0, 0, 0.12)' } }}
      >
        <DialogTitle
          sx={{
            textAlign: 'center',
            py: 3,
            backgroundColor: '#fff8e1',
            borderBottom: '1px solid #ffe082',
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1 }}>
            <StarRateIcon sx={{ color: '#f9a825', fontSize: 28 }} />
            <Typography variant="h5" fontWeight={600} sx={{ color: '#f57f17' }}>
              Add Rating
            </Typography>
          </Box>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            Add a manual hotel review for a property.
          </Typography>
        </DialogTitle>
        <DialogContent sx={{ pt: 4, pb: 3, px: 3, overflow: 'visible' }}>
          <TextField
            fullWidth
            label="Hotel ID"
            type="number"
            value={addRatingHotelId}
            onChange={(e) => setAddRatingHotelId(e.target.value)}
            placeholder="e.g. 123"
            variant="outlined"
            InputLabelProps={{ shrink: true }}
            inputProps={{ min: 1, step: 1 }}
            sx={{ mb: 2.5 }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <BusinessIcon sx={{ color: '#f9a825' }} />
                </InputAdornment>
              ),
            }}
          />
          <TextField
            fullWidth
            label="Guest Name"
            value={addRatingGuestName}
            onChange={(e) => setAddRatingGuestName(e.target.value)}
            placeholder="e.g. Rahul S."
            variant="outlined"
            InputLabelProps={{ shrink: true }}
            sx={{ mb: 2.5 }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <PersonIcon sx={{ color: '#f9a825' }} />
                </InputAdornment>
              ),
            }}
          />
          <Grid container spacing={2} sx={{ mb: 2.5 }}>
            <Grid item xs={6}>
              <TextField
                fullWidth
                label="Overall Rating"
                type="number"
                value={addRatingOverall}
                onChange={(e) => setAddRatingOverall(e.target.value)}
                variant="outlined"
                InputLabelProps={{ shrink: true }}
                inputProps={{ min: 1, max: 5, step: 1 }}
                helperText="1–5"
              />
            </Grid>
            <Grid item xs={6}>
              <TextField
                fullWidth
                label="Cleanliness"
                type="number"
                value={addRatingCleanliness}
                onChange={(e) => setAddRatingCleanliness(e.target.value)}
                variant="outlined"
                InputLabelProps={{ shrink: true }}
                inputProps={{ min: 1, max: 5, step: 1 }}
                helperText="1–5"
              />
            </Grid>
            <Grid item xs={6}>
              <TextField
                fullWidth
                label="Service"
                type="number"
                value={addRatingService}
                onChange={(e) => setAddRatingService(e.target.value)}
                variant="outlined"
                InputLabelProps={{ shrink: true }}
                inputProps={{ min: 1, max: 5, step: 1 }}
                helperText="1–5"
              />
            </Grid>
            <Grid item xs={6}>
              <TextField
                fullWidth
                label="Comfort"
                type="number"
                value={addRatingComfort}
                onChange={(e) => setAddRatingComfort(e.target.value)}
                variant="outlined"
                InputLabelProps={{ shrink: true }}
                inputProps={{ min: 1, max: 5, step: 1 }}
                helperText="1–5"
              />
            </Grid>
            <Grid item xs={6}>
              <TextField
                fullWidth
                label="Value"
                type="number"
                value={addRatingValue}
                onChange={(e) => setAddRatingValue(e.target.value)}
                variant="outlined"
                InputLabelProps={{ shrink: true }}
                inputProps={{ min: 1, max: 5, step: 1 }}
                helperText="1–5"
              />
            </Grid>
          </Grid>
          <TextField
            fullWidth
            label="Insert Time"
            type="datetime-local"
            value={addRatingInsertTime}
            onChange={handleAddRatingInsertTimeChange}
            variant="outlined"
            InputLabelProps={{ shrink: true }}
            inputProps={{ max: formatDateTimeLocal() }}
            helperText="Defaults to now. Future date/time is not allowed."
            sx={{ mb: 2.5 }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <ScheduleIcon sx={{ color: '#f9a825' }} />
                </InputAdornment>
              ),
            }}
          />
          <TextField
            fullWidth
            label="Review Text"
            value={addRatingText}
            onChange={(e) => setAddRatingText(e.target.value)}
            placeholder="Great stay, would visit again."
            variant="outlined"
            multiline
            minRows={3}
            InputLabelProps={{ shrink: true }}
          />
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2, borderTop: '1px solid #eee', gap: 1 }}>
          <Button onClick={handleAddRatingClose} variant="outlined" disabled={addRatingLoading}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleAddRatingSubmit}
            disabled={
              addRatingLoading ||
              !addRatingHotelId.trim() ||
              !addRatingGuestName.trim() ||
              !addRatingInsertTime ||
              !addRatingText.trim()
            }
            sx={{ backgroundColor: '#f9a825', '&:hover': { backgroundColor: '#f57f17' } }}
          >
            {addRatingLoading ? (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <CircularProgress size={16} sx={{ color: 'white' }} />
                Submitting...
              </Box>
            ) : (
              'Add Review'
            )}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar for notifications */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <Alert 
          onClose={() => setSnackbar({ ...snackbar, open: false })} 
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default HomePage; 